import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enGB from 'date-fns/locale/en-GB';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import TransliteratedInput from './TransliteratedInput';
import SignatureCanvas from './SignatureCanvas';
import PhotoUpload from './PhotoUpload';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useTeluguYear } from '../context/TeluguYearContext';
import GrandhamTreeData from './GrandhamTreeData';
import api from '../utils/api';
import { getKrishnaGrandhamInfo } from '../utils/krishnaYajurvedaUtils';
import {
  krishna_yajurvedam_tree_data,
  krishna_yajurvedam_tree_labels,
  lakshanm_tree_data,
  lakshanm_tree_labels,
  lakshanam_variant_labels,
  sanskrit_tree_data,
  sanskrit_tree_labels,
  vedadhyayi_tree_data,
  vedadhyayi_tree_labels
} from '../data/grandhamData';
import { getGrandhamData } from '../utils/grandhamDataUtils';

const gurukulam_patahasala_id = "6946b170dac0c1f33389a370"; // Gurukulam pathasala id

const grandhamLabels = {
  telugu: {
    pannamu: 'వ ప్రశ్న',
    adhyayam: 'వ అధ్యాయం',
    prakaranam: 'వ ప్రకరణం',
    accordionTitle: 'పూర్వము(పాత) మరియు ప్రస్తుతము (క్రొత్త) పరీక్ష గ్రంథ వివరములు',
    presentTitle: 'ప్రస్తుతము (క్రొత్త) పరీక్ష ఇవ్వబోవు గ్రంథ వివరము',
    previousTitle: 'సంస్థలో పూర్వము (పాత) పరీక్ష ఇచ్చిన గ్రంథ వివరము',
    padamu: 'పదము',
    kramamu: 'క్రమము',
    jata: 'జటా',
    ghanamu: 'ఘనము'
  },
  sanskrit: {
    pannamu: ' प्रश्नः',
    adhyayam: ' अध्यायः',
    prakaranam: ' प्रकरणम्',
    accordionTitle: 'पूर्व/वर्तमान परीक्षा ग्रन्थविवरणानि',
    presentTitle: 'वर्तमानपरीक्षायाः ग्रन्थविवरणम्',
    previousTitle: 'संस्थायां पूर्वपरीक्षायाः ग्रन्थविवरणम्',
    padamu: 'पदम्',
    kramamu: 'क्रमम्',
    jata: 'जटा',
    ghanamu: 'घनम्'
  }
};

const vatuAccordionLabels = {
  telugu: {
    title: 'విద్యార్థి మరియు పాఠశాల వివరములు',
    complete: 'పూర్తి',
    pending: 'అసంపూర్ణం',
    allFieldsCompleted: 'కావలసిన అన్నీ వివరాలు నమోదు చేసారు.',
    fillAllFields: 'కావలసిన అన్నీ వివరాలను నమోదు చేయండి.'
  },
  sanskrit: {
    title: 'वटोः तथा च पाठशालाविवरणानि',
    complete: 'पूर्णम्',
    pending: 'अपूर्णम्',
    allFieldsCompleted: 'आवश्यकानि सर्वाणि विवरणानि प्रविष्टानि सन्ति।',
    fillAllFields: 'आवश्यकानि सर्वाणि विवरणानि प्रविशतु।'
  }
};

// Map each exam type to its checkbox tree data and localized labels.
const grandhamTreeConfig = {
  krishna_yajurveda: {
    data: krishna_yajurvedam_tree_data,
    labels: krishna_yajurvedam_tree_labels
  },
  lakshanam: {
    data: lakshanm_tree_data,
    labels: lakshanm_tree_labels,
    variantLabels: lakshanam_variant_labels
  },
  sanskrit: {
    data: sanskrit_tree_data,
    labels: sanskrit_tree_labels
  },
  vedadhyayi_sahityam: {
    data: vedadhyayi_tree_data,
    labels: vedadhyayi_tree_labels
  }
};

// Define LocalizedInput outside VatuForm to prevent recreation on every render
function LocalizedInput({ value, setValue, labels, placeholders, multiline, minRows, lang, onFocus, onBlur, disabled = false }) {
  const label = lang === 'telugu' ? labels.telugu : labels.sanskrit;
  const placeholder = placeholders ? (lang === 'telugu' ? placeholders.telugu : placeholders.sanskrit) : undefined;
  return (
    <TransliteratedInput 
      label={label} 
      value={value} 
      onChange={setValue}
      onSuggestionSelect={setValue}
      placeholder={placeholder}
      targetScript={lang === 'telugu' ? 'telugu' : 'devanagari'}
      multiline={multiline}
      minRows={minRows}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={disabled}
    />
  );
}


// Build grandham payload using pariksha-type-specific handlers
// Moved inside VatuForm to access state helpers


export default function VatuForm({ onSubmit, editVatu }) {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const { teluguYearTelugu, teluguYearSanskrit } = useTeluguYear();
  
  // fields in the same order as the model (skipping vatu_id)
  const [vatu_name, setVatuName] = useState('');
  const [date_of_birth, setDateOfBirth] = useState('');
  const [calculatedAge, setCalculatedAge] = useState('');
  const ageNum = Number(calculatedAge) || 0;
  const [aadhar, setAadhar] = useState('');
  const [aadharFile, setAadharFile] = useState(null); // File object
  const [aadharFileName, setAadharFileName] = useState('');
  const [aadharFileDownloadLink, setAadharFileDownloadLink] = useState(null);
  const [krishnaYajurvedaPattaaFileName, setKrishnaYajurvedaPattaaFileName] = useState('');
  const [krishnaYajurvedaPattaaFileDownloadLink, setKrishnaYajurvedaPattaaFileDownloadLink] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [district, setDistrict] = useState('');
  const [gothram, setGothram] = useState('');
  const [sutram, setSutram] = useState('');
  const [swasakha, setSwasakha] = useState('');
  const [father_name, setFatherName] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [mother_name, setMotherName] = useState('');
  const [mother_maiden_name, setMotherMaidenName] = useState('');
  const [mother_gothram, setMotherGothram] = useState('');
  const [guruvu_gari_name, setGuruvuGariName] = useState('');
  const [pathasala, setPathasala] = useState({pathasala_id: '', pathasala_name: ''});
  const [other_details, setOtherDetails] = useState('');
  
  // Pathasala list for dropdown
  const [pathasalaList, setPathasalaList] = useState([]);

  // Every Vatu API request must have a pathasala. Users without an assigned
  // pathasala are treated as Gurukulam users.
  const resolvedPathasalaId = user?.pathasala_id || pathasala?.pathasala_id || gurukulam_patahasala_id;
  
  // Guruvu Garu names and signatures for PathashalaAdmin
  const [guruvuGaruNames, setGuruvuGaruNames] = useState({}); // { "గురువు పేరు": signature_download_link, ... }
  const [guruvuSignatures, setGuruvuSignatures] = useState({}); // { "గురువు పేరు": signature_download_link, ... }
  const [adminSignatureFetched, setAdminSignatureFetched] = useState(null); // Admin signature download link
  const [showGuruvuSuggestions, setShowGuruvuSuggestions] = useState(false);
  const [showDeleteGuruvuDialog, setShowDeleteGuruvuDialog] = useState(false);
  const [guruvuToDelete, setGuruvuToDelete] = useState('');
  
  // Admin signature from pathasala (used as fallback when pathasala_signature is empty)
  const [adminSignatureFromPathasala, setAdminSignatureFromPathasala] = useState('');
  // Ref to avoid stale closure issues in aadhar effect
  const adminSignatureRef = useRef('');

  // Photo and Signatures
  const [photo, setPhoto] = useState(null); // File object
  const [photoConfig, setPhotoConfig] = useState({ fit: 'cover', position: 'center' });
  const [vatu_signature, setVatuSignature] = useState(null); // File object
  const [guruvu_signature, setGuruvuSignature] = useState(null); // File object
  const [pathasala_signature, setPathasalaSignature] = useState(null); // File object

  // Phone with country code
  const [phoneLocal, setPhoneLocal] = useState('');

  // Pariksha type selection (multiselect)
  const [parikshaType, setParikshaType] = useState([]);
  // Store all current and previous exam tree selections in one state object.
  const [grandhamSelections, setGrandhamSelections] = useState({});
  const [grandhamData, setGrandhamData] = useState({});

  // Country code removed; phone input will default to +91 prefix visually

  const [parikshaTypeOptions, setParikshaTypeOptions] = useState([]);
  const parikshaTypesFetched = useRef(false);

  // Fetch pariksha types from API on component mount (prevent duplicate calls)
  useEffect(() => {
    // Guard against multiple calls in strict mode or remounting
    if (parikshaTypesFetched.current) {
      return;
    }
    parikshaTypesFetched.current = true;

    const fetchParikshaTypes = async () => {
      try {
        const res = await api.get('/api/pariksha-types');
        const data = await res.json();
        
        if (res.ok && data.success && Array.isArray(data.data)) {
          setParikshaTypeOptions(data.data);
        } else {
          console.error('Failed to fetch pariksha types:', data.error);
          // Fallback to default options if API fails
          setParikshaTypeOptions([
            { value: 'krishna_yajurveda', label: { telugu: 'కృష్ణ యజుర్వేదం', sanskrit: 'कृष्ण यजुर्वेदम्' } },
            { value: 'lakshanam', label: { telugu: 'లక్షణం', sanskrit: 'लक्षणम्' } },
            { value: 'sanskrit', label: { telugu: 'సంస్కృతం', sanskrit: 'संस्कृतम्' } },
            { value: 'vedadhyayi_sahityam', label: { telugu: 'వేదాధ్యాయి సాహిత్యం', sanskrit: 'वेदाध्यायी साहित्यम्' } },
          ]);
        }
      } catch (err) {
        console.error('Error fetching pariksha types:', err);
        // Fallback to default options if API fails
        setParikshaTypeOptions([
          { value: 'krishna_yajurveda', label: { telugu: 'కృష్ణ యజుర్వేదం', sanskrit: 'कृष्ण यजुर्वेदम्' } },
          { value: 'lakshanam', label: { telugu: 'లక్షణం', sanskrit: 'लक्షणम्' } },
          { value: 'sanskrit', label: { telugu: 'సంస్కృతం', sanskrit: 'संस्कృतम्' } },
          { value: 'vedadhyayi_sahityam', label: { telugu: 'వేదాధ్యాయి సాహిత్యం', sanskrit: 'वेदाध्यायी साहित्यम्' } },
        ]);
      }
    };

    fetchParikshaTypes();
  }, []);

  // Fetch admin config and check if today is a pariksha_date and if registrations are closed
  useEffect(() => {
    const fetchAdminConfigAndCheckDate = async () => {
      try {
        // Check if user is Admin or SuperUser
        const isAdmin = user && (user.role === 'Admin' || user.role === 'SuperUser');
        setIsAdminOnly(isAdmin);

        // Fetch active admin config
        const res = await api.get('/api/admin-config/active');
        const data = await res.json();
        
        if (res.ok && data.success && data.data) {
          setAdminConfig(data.data);
          
          // Check if registrations are closed (current date > registrations_end date)
          if (data.data.registrations_end) {
            const today = new Date();
            // Parse DD-MM-YYYY format
            const parts = data.data.registrations_end.split('-');
            const registrationsEndDate = new Date(parts[2], parts[1] - 1, parts[0]); // Year, Month (0-based), Day
            
            registrationsEndDate.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            const isClosed = today > registrationsEndDate;
            setRegistrationsClosed(isClosed);
            // Debug log
            console.log('Registration date check:', {
              rawValue: data.data.registrations_end,
              today: today.toLocaleDateString('en-IN'),
              registrationsEndDate: registrationsEndDate.toLocaleDateString('en-IN'),
              isClosed: isClosed
            });
          }
          
          // Check if today is a pariksha_date
          if (data.data.pariksha_dates && Array.isArray(data.data.pariksha_dates)) {
            const today = new Date();
            const todayString = today.toLocaleDateString('en-CA'); // 'en-CA' locale gives YYYY-MM-DD
            const isToday = data.data.pariksha_dates.some(dateStr => {
              const d = new Date(dateStr);
              return d.toLocaleDateString('en-CA') === todayString;
            });
            setShowAdminFieldsToday(isToday);
          }
        }
      } catch (err) {
        console.error('Error fetching admin config:', err);
      }
    };

    fetchAdminConfigAndCheckDate();
  }, [pathasala]);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const [loading, setLoading] = useState(false);
  
  // If age changes and previously selected pariksha types exceed limits, remove them
  useEffect(() => {
    const removed = [];
    const allowed = parikshaType.filter(pt => {
      if (pt === 'sanskrit' && ageNum > 30) {
        removed.push(pt);
        return false;
      }
      if (pt === 'lakshanam' && ageNum > 40) {
        removed.push(pt);
        return false;
      }
      if (pt === 'krishna_yajurveda' && ageNum > 35) {
        removed.push(pt);
        return false;
      }
      if (pt === 'vedadhyayi_sahityam' && ageNum > 30) {
        removed.push(pt);
        return false;
      }
      return true;
    });
    if (removed.length) {
      setParikshaType(allowed);
      setToast({ open: true, message: (lang === 'telugu' ? 'వయస్సు పరిమితి కారణంగా కొంత ఎంపిక తొలగించబడింది' : 'आयु सीमा कारणं केचन विकल्पाः अपहरिताः'), severity: 'warning' });
    }
  }, [ageNum]);

  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [submitSuccessOpen, setSubmitSuccessOpen] = useState(false);

  // Admin-only optional fields (not included in form completion status)
  const [adminConfig, setAdminConfig] = useState(null); // Admin config with pariksha_dates
  const currentGrandhamYear = (() => {
    const issueDate = adminConfig?.patta_issue_date;
    if (!issueDate) return '';
    const dayMonthYear = String(issueDate).match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dayMonthYear) return dayMonthYear[3];
    const parsedDate = new Date(issueDate);
    return Number.isNaN(parsedDate.getTime()) ? '' : String(parsedDate.getFullYear());
  })();
  const [isAdminOnly, setIsAdminOnly] = useState(false); // Show admin-only fields only to Admin/SuperUser
  const [attendance, setAttendance] = useState(false); // Checkbox for attendance
  const [panasalaAntyakshari, setPanasalaAntyakshari] = useState(''); // 'junior', 'senior', or 'not_interested'
  const [showAdminFieldsToday, setShowAdminFieldsToday] = useState(false); // True if today is a pariksha_date
  const [isAdminAccordionComplete, setIsAdminAccordionComplete] = useState(false); // Admin accordion completion status
  const [travelExpenses, setTravelExpenses] = useState([]); // Travel expenses list from mongo
  const [prantham, setPrantham] = useState(''); // Travel expense amount selected
  const [registrationsClosed, setRegistrationsClosed] = useState(false); // True if registration deadline has passed
  
  // Determine if form should be read-only (Admin or SuperUser viewing an existing vatu)
  const isReadOnly = editVatu && user && (user.role === 'Admin' || user.role === 'SuperUser');
  
  // Allow specific file fields to be editable for Admin/SuperUser on pariksha dates
  const isAdminOnParikshaDate = isAdminOnly && showAdminFieldsToday;
  const canAdminEditFiles = isReadOnly && isAdminOnParikshaDate;

  // Fetch travel expenses on component mount
  useEffect(() => {
    const fetchTravelExpenses = async () => {
      try {
        const response = await api.get('/api/travel-expenses/vatu_travel');
        const data = await response.json();
        console.log('Fetched travel expenses:', data);
        if (response.ok && Array.isArray(data)){
        setTravelExpenses(data);
        }else { setTravelExpenses([]);  }
      } catch (error) {
        console.error('Error fetching travel expenses:', error);
        setTravelExpenses([]); // Set empty array on error to prevent crashes
      }
    };
    if (!travelExpenses.length) fetchTravelExpenses();
  }, [isAdminOnly]);

  // Compute completeness of Admin accordion
  useEffect(() => {
    if (!isAdminOnly || !showAdminFieldsToday) {
      // If admin accordion is not shown, it's considered complete (no requirement)
      setIsAdminAccordionComplete(true);
      return;
    }
    // Both attendance and panasalaAntyakshari must be filled
    const adminComplete = attendance && panasalaAntyakshari[teluguYearTelugu] && panasalaAntyakshari[teluguYearTelugu] !== '' && prantham;
    setIsAdminAccordionComplete(adminComplete);
  }, [isAdminOnly, showAdminFieldsToday, attendance, panasalaAntyakshari, prantham]);
  
  // Grandham Details - SEPARATE states for CURRENT exam of each pariksha type
  const [krishnaYajurvedaCurrentGrandhams, setKrishnaYajurvedaCurrentGrandhams] = useState({});
  
  // Lakshanam has two modes: కంఠస్తం (kanthastham) and వ్యాఖ్యానం (vyakhyanam)
  const [lakshanamCurrentMode, setLakshanamCurrentMode] = useState('kanthastham'); // 'kanthastham' or 'vyakhyanam'
  const [lakshanamCurrentKanthastham, setLakshanamCurrentKanthastham] = useState({});
  const [lakshanamCurrentVyakhyanam, setLakshanamCurrentVyakhyanam] = useState({});
  
  const [sanskritCurrentGrandhams, setSanskritCurrentGrandhams] = useState({});
  const [vedadhyayiCurrentGrandhams, setVedadhyayiCurrentGrandhams] = useState({});
  
  // Grandham Details - SEPARATE states for PREVIOUS exam of each pariksha type
  const [krishnaYajurvedaPreviousGrandhams, setKrishnaYajurvedaPreviousGrandhams] = useState({});
  
  // Lakshanam previous also has two modes
  const [lakshanamPreviousMode, setLakshanamPreviousMode] = useState('kanthastham'); // 'kanthastham' or 'vyakhyanam'
  const [lakshanamPreviousKanthastham, setLakshanamPreviousKanthastham] = useState({});
  const [lakshanamPreviousVyakhyanam, setLakshanamPreviousVyakhyanam] = useState({});

  const [sanskritPreviousGrandhams, setSanskritPreviousGrandhams] = useState({});
  const [vedadhyayiPreviousGrandhams, setVedadhyayiPreviousGrandhams] = useState({});

  // Grandham accordion state
  const [grandhamExpanded, setGrandhamExpanded] = useState(false);
  const [isGrandhamComplete, setIsGrandhamComplete] = useState(false);

  // Pathashala confirmation dialog
  const [showPathasalaDialog, setShowPathasalaDialog] = useState(false);
  const [showGurukshaDialog, setShowGurukshaDialog] = useState(false);
  // Kramantham attestation dialog state
  const [showKramanthamDialog, setShowKramanthamDialog] = useState(false);
  const [pendingKramantham, setPendingKramantham] = useState('');
  const [attestationChecked, setAttestationChecked] = useState(false);

  // Krishna Yajurvedam cascade states
  const [krishnaYajurvedaType, setKrishnaYajurvedaType] = useState(''); // 'first_varshika_pariksha' or 'pattaa_pariksha'
  const [krishnaYajurvedaKramantham, setKrishnaYajurvedaKramantham] = useState(''); // 'vishesha_kramantham', 'saamaanya_kramantham', 'vishesha_ghana', 'saamaanya_ghana'
  const [krishnaYajurvedaPattaaFile, setKrishnaYajurvedaPattaaFile] = useState(null); // File object for Vishesha Ghana

  const hasGrandhamSelection = (value) => {
    if (value === true) return true;
    if (Array.isArray(value)) return value.length > 0;
    if (!value || typeof value !== 'object') return false;
    return Object.values(value).some(hasGrandhamSelection);
  };

  const withoutEmptySelections = (value) => {
    if (!value || typeof value !== 'object') return value;
    if (Array.isArray(value)) return value;
    return Object.fromEntries(
      Object.entries(value)
        .map(([key, child]) => [key, withoutEmptySelections(child)])
        .filter(([, child]) => hasGrandhamSelection(child))
    );
  };

  // Update one exam type and keep the legacy type-specific states synchronized.
  const updateGrandhamSelections = (phase, type, selections) => {
    const variant = type === 'lakshanam'
      ? (phase === 'current' ? lakshanamCurrentMode : lakshanamPreviousMode)
      : null;
    const cleanSelections = withoutEmptySelections(selections);

    setGrandhamSelections((previous) => {
      const phaseSelections = type === 'lakshanam'
        ? {
            ...(previous[type]?.[phase] || {}),
            ...(hasGrandhamSelection(cleanSelections) ? { [variant]: cleanSelections } : {})
          }
        : cleanSelections;
      if (type === 'lakshanam' && !hasGrandhamSelection(cleanSelections)) {
        delete phaseSelections[variant];
      }
      const typeSelections = {
        ...(previous[type] || {}),
        ...(hasGrandhamSelection(phaseSelections) ? { [phase]: phaseSelections } : {})
      };
      if (!hasGrandhamSelection(phaseSelections)) delete typeSelections[phase];

      const next = {
        ...previous,
        ...(hasGrandhamSelection(typeSelections) ? { [type]: typeSelections } : {})
      };
      if (!hasGrandhamSelection(typeSelections)) delete next[type];
      const cleanNext = withoutEmptySelections(next);
      console.log('[grandhamSelections updated]', cleanNext);
      return cleanNext;
    });

    setGrandhamData((previousData) => {
      const existingPhase = previousData[type]?.[phase] || {};
      let phaseData;
      if (type === 'lakshanam') {
        phaseData = {
          ...existingPhase,
          ...(hasGrandhamSelection(cleanSelections) ? { [variant]: cleanSelections } : {})
        };
        if (!hasGrandhamSelection(cleanSelections)) delete phaseData[variant];
      } else {
        const hasYearBucket = Object.keys(cleanSelections || {}).some((key) => /^\d{4}$/.test(key));
        const existingYear = Object.keys(existingPhase).find((key) => /^\d{4}$/.test(key));
        const year = existingYear || (
          currentGrandhamYear ? String(Number(currentGrandhamYear) - 1) : ''
        );
        phaseData = phase === 'previous' && !hasYearBucket
          ? (year ? { [year]: cleanSelections } : cleanSelections)
          : cleanSelections;
      }
      if (!hasGrandhamSelection(phaseData)) {
        const next = { ...previousData, [type]: { ...(previousData[type] || {}) } };
        delete next[type][phase];
        if (!hasGrandhamSelection(next[type])) delete next[type];
        return withoutEmptySelections(next);
      }
      return withoutEmptySelections({
        ...previousData,
        [type]: { ...previousData[type], [phase]: phaseData }
      });
    });

    if (phase === 'current') {
      const currentSelection = selections[currentGrandhamYear] || {};
      if (type === 'krishna_yajurveda') setKrishnaYajurvedaCurrentGrandhams(currentSelection);
      if (type === 'lakshanam') {
        if (lakshanamCurrentMode === 'kanthastham') setLakshanamCurrentKanthastham(currentSelection);
        else setLakshanamCurrentVyakhyanam(currentSelection);
      }
      if (type === 'sanskrit') setSanskritCurrentGrandhams(currentSelection);
      if (type === 'vedadhyayi_sahityam') setVedadhyayiCurrentGrandhams(currentSelection);
    } else {
      const previousSelection = Object.values(selections).find(
        (value) => value && typeof value === 'object' && !Array.isArray(value)
      ) || {};
      if (type === 'krishna_yajurveda') setKrishnaYajurvedaPreviousGrandhams(previousSelection);
      if (type === 'lakshanam') {
        if (lakshanamPreviousMode === 'kanthastham') setLakshanamPreviousKanthastham(previousSelection);
        else setLakshanamPreviousVyakhyanam(previousSelection);
      }
      if (type === 'sanskrit') setSanskritPreviousGrandhams(previousSelection);
      if (type === 'vedadhyayi_sahityam') setVedadhyayiPreviousGrandhams(previousSelection);
    }
  };

  // Restore saved grandham selections from the API into both phases.
  const bindGrandhamFromAPI = (savedGrandhams, phase) => {
    const saved = savedGrandhams && typeof savedGrandhams === 'object' ? savedGrandhams : {};
    Object.keys(grandhamTreeConfig).forEach((type) => {
      const typeSelections = saved[type] || {};
      const phaseSelections = typeSelections[phase] ||
        (Object.keys(typeSelections).some((key) => /^\d{4}$/.test(key)) ? typeSelections : {});
      if (type === 'lakshanam') {
        const hasVariants = ['kanthastham', 'vyakhyanam'].some((variant) => phaseSelections[variant]);
        const variantSelections = hasVariants
          ? phaseSelections
          : { kanthastham: phaseSelections };
        const cleanVariantSelections = withoutEmptySelections(variantSelections);
        setGrandhamSelections((previous) => {
          const next = { ...previous };
          const typeSelections = { ...(previous[type] || {}) };
          if (hasGrandhamSelection(cleanVariantSelections)) {
            typeSelections[phase] = cleanVariantSelections;
            next[type] = typeSelections;
          } else {
            delete typeSelections[phase];
            if (hasGrandhamSelection(typeSelections)) next[type] = typeSelections;
            else delete next[type];
          }
          return withoutEmptySelections(next);
        });
        setGrandhamData((previous) => {
          const next = { ...previous };
          const typeSelections = { ...(previous[type] || {}) };
          if (hasGrandhamSelection(cleanVariantSelections)) {
            typeSelections[phase] = cleanVariantSelections;
            next[type] = typeSelections;
          } else {
            delete typeSelections[phase];
            if (hasGrandhamSelection(typeSelections)) next[type] = typeSelections;
            else delete next[type];
          }
          return withoutEmptySelections(next);
        });
        return;
      }
      updateGrandhamSelections(phase, type, phaseSelections);
    });
  };

  const getLakshanamVariantSelections = (phase, variant) =>
    grandhamSelections.lakshanam?.[phase]?.[variant] || {};

  const handleLakshanamVariantChange = (phase, variant) => {
    if (!variant) return;
    if (phase === 'current') setLakshanamCurrentMode(variant);
    else setLakshanamPreviousMode(variant);
  };

  // Return the selected grandham trees in the payload shape expected by the API.
  const buildGrandhamPayload = (types, selections = grandhamSelections) =>
    types.reduce((result, type) => {
      if (selections[type]) result[type] = selections[type];
      return result;
    }, {});


  // When user makes selections in vyakhyanam mode, also sync them to kanthastham with disabled flag
  useEffect(() => {
    if (lakshanamCurrentMode === 'vyakhyanam') {
      console.log('\n🔄 [SYNC] vyakhyanam → kanthastham (state changed)');
      console.log('📊 Current vyakhyanam:', JSON.stringify(lakshanamCurrentVyakhyanam, null, 2));
      
      // Start with current kanthastham
      const syncedKanthastham = JSON.parse(JSON.stringify(lakshanamCurrentKanthastham || {}));
      
      // First, REMOVE all old synced items (those with _disabledFromVyakhyanam flag)
      const removeOldSyncedItems = (obj) => {
        if (!obj || typeof obj !== 'object') return;
        
        Object.keys(obj).forEach(key => {
          if (key === '_selected' || key === '_autoSelected') return;
          
          const value = obj[key];
          if (typeof value === 'object' && value !== null) {
            if (value._disabledFromVyakhyanam === true) {
              // Remove this item since it was synced
              delete obj[key];
              console.log(`  🗑️ Removed old synced item: ${key}`);
            } else {
              // Recurse to check nested items
              removeOldSyncedItems(value);
            }
          }
        });
      };
      
      removeOldSyncedItems(syncedKanthastham);
      
      // Now ADD the current vyakhyanam selections
      const syncSelectionsWithDisabledFlag = (srcObj, destObj) => {
        if (!srcObj || typeof srcObj !== 'object') return;
        
        Object.keys(srcObj).forEach(key => {
          if (key === '_selected' || key === '_autoSelected') return;
          
          const srcValue = srcObj[key];
          if (typeof srcValue === 'object' && srcValue !== null) {
            if (srcValue._selected === true) {
              // Mark with disabled flag
              if (!destObj[key]) destObj[key] = {};
              Object.assign(destObj[key], srcValue);
              destObj[key]._disabledFromVyakhyanam = true;
              console.log(`  ✅ Synced selection: ${key}`);
            } else if (srcValue._selected !== false) {
              // It's a container, recurse
              if (!destObj[key]) destObj[key] = {};
              syncSelectionsWithDisabledFlag(srcValue, destObj[key]);
            }
          }
        });
      };
      
      syncSelectionsWithDisabledFlag(lakshanamCurrentVyakhyanam, syncedKanthastham);
      
      console.log('📝 After sync, kanthastham has keys:', Object.keys(syncedKanthastham));
      setLakshanamCurrentKanthastham(syncedKanthastham);
    }
  }, [lakshanamCurrentVyakhyanam, lakshanamCurrentMode]);

  // Same for previous phase
  useEffect(() => {
    if (lakshanamPreviousMode === 'vyakhyanam') {
      console.log('\n🔄 [SYNC] previous vyakhyanam → kanthastham (state changed)');
      
      const syncedKanthastham = JSON.parse(JSON.stringify(lakshanamPreviousKanthastham || {}));
      
      // First, REMOVE all old synced items
      const removeOldSyncedItems = (obj) => {
        if (!obj || typeof obj !== 'object') return;
        
        Object.keys(obj).forEach(key => {
          if (key === '_selected' || key === '_autoSelected') return;
          
          const value = obj[key];
          if (typeof value === 'object' && value !== null) {
            if (value._disabledFromVyakhyanam === true) {
              delete obj[key];
              console.log(`  🗑️ Removed old synced item: ${key}`);
            } else {
              removeOldSyncedItems(value);
            }
          }
        });
      };
      
      removeOldSyncedItems(syncedKanthastham);
      
      // Now ADD current vyakhyanam selections
      const syncSelectionsWithDisabledFlag = (srcObj, destObj) => {
        if (!srcObj || typeof srcObj !== 'object') return;
        
        Object.keys(srcObj).forEach(key => {
          if (key === '_selected' || key === '_autoSelected') return;
          
          const srcValue = srcObj[key];
          if (typeof srcValue === 'object' && srcValue !== null) {
            if (srcValue._selected === true) {
              if (!destObj[key]) destObj[key] = {};
              Object.assign(destObj[key], srcValue);
              destObj[key]._disabledFromVyakhyanam = true;
            } else if (srcValue._selected !== false) {
              if (!destObj[key]) destObj[key] = {};
              syncSelectionsWithDisabledFlag(srcValue, destObj[key]);
            }
          }
        });
      };
      
      syncSelectionsWithDisabledFlag(lakshanamPreviousVyakhyanam, syncedKanthastham);
      setLakshanamPreviousKanthastham(syncedKanthastham);
    }
  }, [lakshanamPreviousVyakhyanam, lakshanamPreviousMode]);

  // Helper function to calculate total pannams from all grandhams
  const calculateTotalPannams = () => {
    // Build current grandham data structure for getKrishnaGrandhamInfo
    const vatuData = {
      grandham_data: {
        krishna_yajurveda: {
          current: { [currentGrandhamYear]: krishnaYajurvedaCurrentGrandhams },
          previous: { [String(Number(currentGrandhamYear) - 1)]: krishnaYajurvedaPreviousGrandhams }
        }
      }
    };

    // Use getKrishnaGrandhamInfo to get pannams for all grandhams
    const grandhamInfos = getKrishnaGrandhamInfo(vatuData, lang);
    
    // Sum up all pannams
    const totalPannams = grandhamInfos.reduce((sum, grandham) => sum + (grandham.pannams || 0), 0);
    
    return totalPannams;
  };

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setToast({ ...toast, open: false });
  };

  // Fetch user's pathasala list on mount
  useEffect(() => {
    const fetchUserPathasalas = async () => {
      try {
        // Set default pathasala name for anonymous or no user
        if (!user || user.role?.toLowerCase() === 'anonymous') {
          setPathasala({pathasala_id: gurukulam_patahasala_id, pathasala_name: 'గురుకులం'});
          return;
        }
        
        // If user has pathasala_name in their profile, use it
        if (user.pathasala_name || user.gurukulam_patahasala_id || !user.pathasala_id) {
          const userPathasalaId = user.pathasala_id || user.gurukulam_patahasala_id || gurukulam_patahasala_id;
          setPathasalaList([{pathasala_id: userPathasalaId, pathasala_name: user.pathasala_name || 'గురుకులం'}]);
          setPathasala({pathasala_id: userPathasalaId, pathasala_name: user.pathasala_name || 'గురుకులం'});
        } 
      } catch (err) {
        console.error('Error fetching pathasala list:', err);
        showToast('Error loading pathasala list', 'error');
      }
    };

    fetchUserPathasalas();
  }, [user]);

  // Fetch guruvu names for PathashalaAdmin
  useEffect(() => {
    const fetchGuruvuNames = async () => {
      if (!user || user.role !== 'PathashalaAdmin') {
        console.log('⏭️  Skipping guruvu fetch: Not PathashalaAdmin');
      return;
    }
    
    try {
      console.log('🔵 Fetching guruvu names for pathasala:', resolvedPathasalaId);
      const token = localStorage.getItem('accessToken');
      console.log('🔑 Token available:', !!token);
      
      const res = await api.get(`/api/pathasala/${resolvedPathasalaId}/guruvu-names`);
      const data = await res.json();
      
      console.log('📥 Guruvu names response:', { status: res.status, data });
      
      if (res.ok && data.success) {
        const responseData = data.data;
        // Handle both old format (direct object) and new format (with guruvu_names and admin_signature)
        if (responseData.guruvu_names) {
          setGuruvuGaruNames(responseData.guruvu_names || {});
          setGuruvuSignatures(responseData.guruvu_names || {});
          setAdminSignatureFetched(responseData.admin_signature || null);
          console.log('✅ Fetched guruvu names with signatures:', Object.keys(responseData.guruvu_names || {}));
          console.log('✅ Admin signature:', responseData.admin_signature);
        } else {
          // Fallback for old format
          setGuruvuGaruNames(responseData || {});
          setGuruvuSignatures(responseData || {});
          console.log('✅ Fetched guruvu names (old format):', Object.keys(responseData || {}));
        }
      } else {
        console.error('❌ Guruvu names fetch failed:', data.message);
        showToast(data.message || 'Failed to fetch guruvu names', 'error');
      }
    } catch (err) {
      console.error('❌ Error fetching guruvu names:', err);
      showToast('Error fetching guruvu names: ' + err.message, 'error');
    }
  };

    fetchGuruvuNames();
  }, [user, resolvedPathasalaId]);

  // Fetch and auto-populate admin signature for PathashalaAdmin (only once on mount)
  useEffect(() => {
    const fetchAdminSignature = async () => {
      if (!user || user.role !== 'PathashalaAdmin') {
        console.log('⏭️  Skipping admin signature fetch: Not PathashalaAdmin');
        return;
      }
      
      // Skip if already fetched from guruvu-names endpoint
      if (adminSignatureFetched) {
        console.log('✅ Admin signature already fetched from guruvu-names endpoint');
        setAdminSignatureFromPathasala(adminSignatureFetched);
        adminSignatureRef.current = adminSignatureFetched;
        // Auto-populate pathasala_signature with stored admin signature only if currently empty
        setPathasalaSignature(prev => prev || adminSignatureFetched);
        return;
      }
      
      // Fallback: fetch directly if not already fetched
      if (adminSignatureFromPathasala) {
        console.log('⏭️  Admin signature already in state');
        return;
      }
      
      try {
        console.log('🔵 Fetching admin signature for pathasala:', resolvedPathasalaId);
        const token = localStorage.getItem('accessToken');
        console.log('🔑 Token available:', !!token);
        
        const res = await api.get(`/api/pathasala/${resolvedPathasalaId}/admin-signature`);
        const data = await res.json();
        
        console.log('📥 Admin signature response:', { status: res.status, data });
        
        if (res.ok && data.success && data.data) {
          // Store admin signature from pathasala for future fallback use
          setAdminSignatureFromPathasala(data.data);
          // Also store in ref to avoid stale closure issues
          adminSignatureRef.current = data.data;
          // Auto-populate pathasala_signature with stored admin signature only if currently empty
          setPathasalaSignature(prev => prev || data.data);
          console.log('✅ Admin signature fetched and set');
        } else {
          console.log('⚠️  No admin signature available or fetch failed');
        }
      } catch (err) {
        console.error('❌ Error fetching admin signature:', err);
        showToast('Error fetching admin signature: ' + err.message, 'error');
      }
    };

    fetchAdminSignature();
  }, [user?.role, resolvedPathasalaId, adminSignatureFetched]);

  // Handler to save guruvu name to pathasala on form save/submit
  const saveGuruvuNameToPathasala = async () => {    
    if (!guruvu_gari_name || !guruvu_gari_name.trim()) {
      console.log('⏭️  Skipping guruvu save: No guruvu name provided');
      return;
    }

    if(!(guruvu_signature instanceof File)) {
      console.log('⏭️  Skipping guruvu save: No New guruvu signature provided');
      return;
    }
    
    try {
      console.log('\n🔵 saveGuruvuNameToPathasala called');
      console.log('📝 Guruvu name:', guruvu_gari_name.trim());
      console.log('🔍 guruvu_signature state:', {
        type: typeof guruvu_signature,
        isFile: guruvu_signature instanceof File,
        isString: typeof guruvu_signature === 'string',
        value: guruvu_signature instanceof File ? `File(${guruvu_signature.name}, ${guruvu_signature.size} bytes)` : guruvu_signature
      });
      
      // Use FormData for multipart upload if file exists
      const formData = new FormData();
      formData.append('guruvu_name', guruvu_gari_name.trim());
      
      // ONLY append file if it's a NEW File object (drawn or uploaded by user)
      // Skip if it's a string URL (already saved in database)
      if (guruvu_signature instanceof File && guruvu_signature.size > 0) {
        formData.append('guruvu_signature', guruvu_signature);
        console.log('✅ NEW signature file detected - Sending File object to API for upload');
      } else {
        console.log('⏭️  No signature available (null/empty)');
      }
      
      console.log('🔵 Sending POST to:', `/api/pathasala/${resolvedPathasalaId}/guruvu-names`);
      const token = localStorage.getItem('accessToken');
      console.log('🔑 Token available:', !!token);
      
      const res = await api.post(`/api/pathasala/${resolvedPathasalaId}/guruvu-names`, formData);
      const responseData = await res.json();
      
      console.log('📥 Save response:', { status: res.status, data: responseData });
      
      if (res.ok && responseData.success) {
        console.log('✅ API call successful - Guruvu name saved to pathasala');
        
        // Update local state with the saved name and signature
        const signatureUrl = guruvu_signature instanceof File 
          ? `/api/pathasala/${resolvedPathasalaId}/download/guruvu/${encodeURIComponent(guruvu_gari_name.trim())}`
          : guruvu_signature;
        
        setGuruvuGaruNames(prev => ({
          ...prev,
          [guruvu_gari_name.trim()]: signatureUrl
        }));
        
        setGuruvuSignatures(prev => ({
          ...prev,
          [guruvu_gari_name.trim()]: signatureUrl
        }));
      } else {
        console.error('❌ API returned error:', responseData.message);
        showToast(responseData.message || 'Error saving guruvu name', 'error');
      }
    } catch (err) {
      console.error('❌ Error saving guruvu name:', err);
      showToast('Error saving guruvu name: ' + err.message, 'error');
    }
  };

  // Handler to save admin signature to pathasala on form save/submit (overwrites existing)
  const saveAdminSignatureToPathasala = async () => {    
    if (!pathasala_signature) {
      console.log('⏭️  Skipping admin signature save: No signature available');
      return;
    }

    if(!(pathasala_signature instanceof File)) {
      console.log('⏭️  Skipping admin signature save: No New signature provided');
      return;
    }
    
    try {
      console.log('\n🔵 saveAdminSignatureToPathasala called');
      console.log('🔍 pathasala_signature state:', {
        type: typeof pathasala_signature,
        isFile: pathasala_signature instanceof File,
        isString: typeof pathasala_signature === 'string',
        value: pathasala_signature instanceof File ? `File(${pathasala_signature.name}, ${pathasala_signature.size} bytes)` : pathasala_signature
      });
      
      // Use FormData for multipart upload if file exists
      const formData = new FormData();
      
      // ONLY append file if it's a NEW File object (drawn or uploaded by user)
      // Skip if it's a string URL (already saved in database)
      if (pathasala_signature instanceof File && pathasala_signature.size > 0) {
        formData.append('pathasala_signature', pathasala_signature);
        console.log('✅ NEW admin signature file detected - Sending File object to API for upload');
      } else if (typeof pathasala_signature === 'string' && pathasala_signature.trim()) {
        console.log('⏭️  Existing admin signature URL - Skipping (already saved in MinIO):', pathasala_signature.substring(0, 50) + '...');
        return;  // Nothing to send, already saved
      } else {
        console.log('⏭️  No admin signature available (null/empty)');
        return;
      }
      
      console.log('🔵 Sending PUT to:', `/api/pathasala/${resolvedPathasalaId}/admin-signature`);
      const token = localStorage.getItem('accessToken');
      console.log('🔑 Token available:', !!token);
      
      const res = await api.put(`/api/pathasala/${resolvedPathasalaId}/admin-signature`, formData);
      const responseData = await res.json();
      
      console.log('📥 Save response:', { status: res.status, data: responseData });
      
      if (res.ok && responseData.success) {
        console.log('✅ API call successful - Admin signature saved to pathasala');
      } else {
        console.error('❌ API returned error:', responseData.message);
        showToast(responseData.message || 'Error saving admin signature', 'error');
      }
    } catch (err) {
      console.error('❌ Error saving admin signature:', err);
      showToast('Error saving admin signature: ' + err.message, 'error');
    }
  };

  // Handler to delete guruvu name from pathasala
  const handleDeleteGuruvuName = async () => {
    if (!user || user.role !== 'PathashalaAdmin' || !guruvuToDelete) {
      setShowDeleteGuruvuDialog(false);
      setGuruvuToDelete('');
      return;
    }
    
    try {
      const res = await api.delete(`/api/pathasala/${resolvedPathasalaId}/guruvu-names/${encodeURIComponent(guruvuToDelete)}`);
      const data = await res.json();
      
      if (res.ok && data.success) {
        // Update both guruvu names and signatures
        const updatedData = data.data || {};
        setGuruvuGaruNames(updatedData);
        setGuruvuSignatures(updatedData);
        showToast(lang === 'telugu' ? 'గురువుగారి పేరు తొలగించబడింది' : 'गुरोः नाम विलोपितम्', 'success');
      } else {
        showToast(data.message || 'Error deleting guruvu name', 'error');
      }
    } catch (err) {
      console.error('Error deleting guruvu name:', err);
      showToast('Error deleting guruvu name', 'error');
    }
    
    setShowDeleteGuruvuDialog(false);
    setGuruvuToDelete('');
  };

  // Handler to select a guruvu from suggestions
  const handleSelectGuruvuSuggestion = (name) => {
    setGuruvuGariName(name);
    
    // Bind the signature for the selected guruvu
    const signature = guruvuSignatures[name];
    if (signature) {
      setGuruvuSignature(signature);
      console.log('✅ Selected guruvu name:', name);
      console.log('✅ Bound signature:', signature);
    } else {
      console.log('✅ Selected guruvu name:', name);
      console.log('⚠️  No signature found for this guruvu');
    }
    
    setShowGuruvuSuggestions(false);
  };

  // Auto-trigger saveGuruvuNameToPathasala when guruvu_signature is uploaded (File object)
  useEffect(() => {
    if (guruvu_signature instanceof File && (user?.role === 'PathashalaAdmin' || canAdminEditFiles) && guruvu_gari_name) {
        console.log('🔔 Auto-triggering saveGuruvuNameToPathasala due to guruvu_signature upload');
        saveGuruvuNameToPathasala();
      }
  }, [guruvu_signature]);

  // Auto-trigger saveAdminSignatureToPathasala when pathasala_signature is uploaded (File object)
  useEffect(() => {
    if (pathasala_signature instanceof File && (user?.role === 'PathashalaAdmin' || canAdminEditFiles)) {
        console.log('🔔 Auto-triggering saveAdminSignatureToPathasala due to pathasala_signature upload');
        saveAdminSignatureToPathasala();
    }
  }, [pathasala_signature]);

  // Populate form when editVatu data is provided
  useEffect(() => {
    console.log('Populate form useEffect triggered', { hasEditVatu: !!editVatu, lang });
    if (editVatu) {
      setVatuName(editVatu.vatu_name || '');
      setDateOfBirth(editVatu.date_of_birth || '');
      setCalculatedAge(editVatu.age || '');
      setAadhar(editVatu.aadhar || '');
      // Don't try to set File objects from API - they will be undefined
      // File objects can only come from user uploads, not from API responses
      setAadharFile(null);
      setAadharFileName(editVatu.aadhar_file_name || '');
      setCompleted(editVatu.completed || false);
      setDistrict(editVatu.district || '');
      setGothram(editVatu.gothram || '');
      setSutram(editVatu.sutram || '');
      setSwasakha(editVatu.swasakha || '');
      setFatherName(editVatu.father_name || '');
      setAddress(editVatu.address || '');
      setEmail(editVatu.email || '');
      setMotherName(editVatu.mother_name || '');
      setMotherMaidenName(editVatu.mother_maiden_name || '');
      setMotherGothram(editVatu.mother_gothram || '');
      setGuruvuGariName(editVatu.guruvu_gari_name || '');
      setPathasala({pathasala_id: editVatu.pathasala_id, pathasala_name: editVatu.pathasala_name} || {pathasala_id: '', pathasala_name: ''});
      setOtherDetails(editVatu.other_details || '');
      // For signatures and photo, use download links for display instead of File objects
      setPhoto(editVatu.photo_download_link || null);
      setPhotoConfig(editVatu.photo_object_config || { fit: 'cover', position: 'center' });
      setVatuSignature(editVatu.vatu_signature_download_link || null);
      setGuruvuSignature(editVatu.guruvu_signature_download_link || null);
      // Use editVatu signature if available, otherwise fallback to admin signature from pathasala
      setPathasalaSignature(editVatu.pathasala_signature_download_link || adminSignatureFromPathasala || null);
      
      // Handle pariksha type
      const parikshaTypeArray = editVatu.pariksha_type 
        ? (Array.isArray(editVatu.pariksha_type) ? editVatu.pariksha_type : [editVatu.pariksha_type])
        : [];
      if (parikshaTypeArray.length > 0) {
        setParikshaType(parikshaTypeArray);
      }
      const savedGrandhamData = getGrandhamData(editVatu);
      setGrandhamData(withoutEmptySelections(savedGrandhamData));
      bindGrandhamFromAPI(savedGrandhamData, 'previous');
      bindGrandhamFromAPI(savedGrandhamData, 'current');

      // Bind Krishna Yajurveda cascade fields if present in payload
      setKrishnaYajurvedaType(editVatu.krishnaYajurvedaType || '');
      setKrishnaYajurvedaKramantham(editVatu.krishnaYajurvedaKramantham || '');
      // File objects can only come from user uploads, not API responses
      setKrishnaYajurvedaPattaaFile(null);
      setKrishnaYajurvedaPattaaFileName(editVatu.krishnaYajurvedaPattaaFile_name || '');
      setKrishnaYajurvedaPattaaFileDownloadLink(editVatu.krishnaYajurvedaPattaaFile_download_link || null);
      
      // Initialize admin-only fields
      // Check if today's date is in attendance_dates array
      const today = new Date().toLocaleDateString('en-CA'); // 'en-CA' locale gives YYYY-MM-DD
      const attendanceDatesArray = Array.isArray(editVatu.attendance_dates) ? editVatu.attendance_dates : [];
      const isTodayPresent = attendanceDatesArray.includes(today);
      setAttendance(isTodayPresent); // Set true if today's date is in attendance_dates
      setPanasalaAntyakshari(editVatu.panasala_antyakshari || '');
      setPrantham(editVatu.prantham || '');
      
      // Handle phone number — normalize to local 10 digits and store in phoneLocal
      if (editVatu.phone) {
        const phoneStr = String(editVatu.phone || '');
        const digits = phoneStr.replace(/\D/g, '');
        setPhoneLocal(digits.slice(-10));
      }
    }
  }, [editVatu]);

  // Field-level validation helpers
  const validateAadhar = (value) => {
    const aadharDigits = String(value || '').replace(/\D/g, '');
    if (!value) return '';
    if (aadharDigits.length !== 12) return 'Aadhar must be exactly 12 digits';
    return '';
  };

  const validatePhone = (value) => {
    const localDigits = String(value || '').replace(/\D/g, '');
    if (!value) return '';
    if (localDigits.length !== 10) return 'Enter a 10-digit local phone number';
    return '';
  };

  const validateEmail = (value) => {
    if (!value) return '';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) return 'Invalid email';
    return '';
  };

  // Note: Language changes are now handled by each handler's language-aware conversion
  // No need to translate state directly since handlers know both languages

  // Compute completeness of the accordion form
  useEffect(() => {
    // Check if aadhar file is either uploaded now or already exists on server
    const hasAadharFile = aadharFile || aadharFileDownloadLink;
    // Check if krishnaYajurvedaPattaaFile is either uploaded now or already exists on server
    const hasKrishnaYajurvedaPattaaFile = krishnaYajurvedaPattaaFile || krishnaYajurvedaPattaaFileDownloadLink;
    const allFields = [
      vatu_name, date_of_birth, aadhar, district, gothram, sutram,
      swasakha, father_name, address, mother_name,
      mother_maiden_name, mother_gothram, guruvu_gari_name,
      pathasala.pathasala_name, hasAadharFile,
      phoneLocal, parikshaType
    ];
    const filled = allFields.every((v) => {
      if (v == null) return false;
      const s = String(v).toString().trim();
      return s.length > 0;
    });
    const phoneOk = validatePhone(phoneLocal) === '';
    const aadharOk = validateAadhar(aadhar) === '';
    const emailOk = !email || validateEmail(email) === '';
    // Photo is valid if it's a File object OR a non-empty string URL
    const hasPhoto = (photo instanceof File) || (typeof photo === 'string' && photo.length > 0);
    // Additional checks for Krishna Yajurveda fields when that pariksha type is selected
    let extraOk = true;
    if (parikshaType && parikshaType.includes && parikshaType.includes('krishna_yajurveda')) {
      // Require the krishnaYajurvedaType to be selected
      if (!krishnaYajurvedaType) extraOk = false;

      // If pattaa pariksha is selected, require kramantham
      if (krishnaYajurvedaType === 'pattaa_pariksha' && !krishnaYajurvedaKramantham) extraOk = false;

      // If vishesha_ghana selected, require the pattaa file
      if (krishnaYajurvedaKramantham === 'vishesha_ghana' && !hasKrishnaYajurvedaPattaaFile) extraOk = false;
    }

    setIsComplete(filled && phoneOk && aadharOk && emailOk && vatu_name && hasPhoto && extraOk);
  }, [
    vatu_name, date_of_birth, aadhar, aadharFile, aadharFileDownloadLink, district, gothram, sutram,
    swasakha, father_name, address, mother_name,
    mother_maiden_name, mother_gothram, guruvu_gari_name,
    pathasala,
    phoneLocal, email, photo, user,
    parikshaType, krishnaYajurvedaType, krishnaYajurvedaKramantham, krishnaYajurvedaPattaaFile, krishnaYajurvedaPattaaFileDownloadLink
  ]);
  
  // Compute completeness of Grandham details accordion
  useEffect(() => {
    // Helper to check if object has any selections
    const hasAnySelection = (obj) => {
      if (obj === true) return true;
      if (Array.isArray(obj)) return obj.length > 0;
      if (!obj || typeof obj !== 'object') return false;
      return Object.keys(obj).some(key => {
        if (key === '_selected' || key === '_noData') return obj[key] === true;
        return hasAnySelection(obj[key]);
      });
    };
    
    // EVERY selected pariksha type must have at least 1 grandham selected in BOTH current AND previous phases
    let allTypesComplete = true;

    parikshaType.forEach((type) => {
      if (!hasAnySelection(grandhamSelections[type]?.current) ||
          !hasAnySelection(grandhamSelections[type]?.previous)) {
        allTypesComplete = false;
      }
    });
    const admin_sign_needed = (user?.role === "PathashalaAdmin" ||  
                (['Admin', 'SuperUser'].includes(user?.role) && editVatu && 
                (pathasala?.pathasala_id !== gurukulam_patahasala_id || pathasala.pathasala_name !== 'గురుకులం')))
    // Check signatures - for anonymous users, don't require pathasala_signature
    const hasSignatures = admin_sign_needed
      ? (!!vatu_signature && !!guruvu_signature && !!pathasala_signature)
      : (!!vatu_signature && !!guruvu_signature);
    
    setIsGrandhamComplete(allTypesComplete && parikshaType.length > 0 && hasSignatures);
  }, [grandhamSelections, parikshaType, vatu_signature, guruvu_signature, pathasala_signature, user, editVatu, pathasala]);

  // Calculate age from date of birth
  useEffect(() => {
    if (date_of_birth) {
      const today = new Date();
      // Parse DD/MM/YYYY format
      let birthDate;
      if (date_of_birth.includes('/')) {
        const [day, month, year] = date_of_birth.split('/');
        birthDate = new Date(year, month - 1, day);
      } else {
        // Fallback for old YYYY-MM-DD format
        birthDate = new Date(date_of_birth);
      }
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      setCalculatedAge(age >= 0 ? age : '');
    } else {
      setCalculatedAge('');
    }
  }, [date_of_birth]);

  console.log("user in VatuForm", user);

  // Fetch existing Vatu data when Aadhar is entered (12 digits)
  useEffect(() => {
    const fetchByAadhar = async () => {
      if (aadhar && aadhar.length === 12) {
        setLoading(true);
        try {
          const params = new URLSearchParams();
          params.append('pathasala_id', resolvedPathasalaId);
          if (lang) params.append('lang', lang);
          const qs = params.toString();
          const res = await api.get(`/api/vatu/aadhar/${aadhar}${qs ? ('?' + qs) : ''}`);
          if (res.ok) {
            const data = await res.json();
            if (data.success && data.data) {
              const v = data.data;
              setVatuName(v.vatu_name || '');
              setDateOfBirth(v.date_of_birth || '');
              setDistrict(v.district || '');
              setGothram(v.gothram || '');
              setSutram(v.sutram || '');
              setSwasakha(v.swasakha || '');
              setFatherName(v.father_name || '');
              setAddress(v.address || '');
              setEmail(v.email || '');
              setMotherName(v.mother_name || '');
              setMotherMaidenName(v.mother_maiden_name || '');
              setMotherGothram(v.mother_gothram || '');
              setGuruvuGariName(v.guruvu_gari_name || '');
              setPathasala({pathasala_id: v.pathasala_id, pathasala_name: v.pathasala_name} || {pathasala_id: '', pathasala_name: ''});
              setOtherDetails(v.other_details || '');
              // Use download links for display instead of File objects
              setPhoto(v.photo_download_link || null);
              setPhotoConfig(v.photo_object_config || { fit: 'cover', position: 'center' });
              setVatuSignature(v.vatu_signature_download_link || null);
              setGuruvuSignature(v.guruvu_signature_download_link || null);
              // Use fetched signature if available, otherwise fallback to admin signature from pathasala
              setPathasalaSignature(v.pathasala_signature_download_link || adminSignatureFromPathasala || null);
              // File objects can only come from user uploads, not API responses
              setAadharFile(null);
              setAadharFileName(v.aadhar_file_name || '');
              setAadharFileDownloadLink(v.aadhar_file_download_link || null);
              setKrishnaYajurvedaPattaaFileName(v.krishnaYajurvedaPattaaFile_name || '');
              setKrishnaYajurvedaPattaaFileDownloadLink(v.krishnaYajurvedaPattaaFile_download_link || null);
              const savedGrandhamData = v.grandham_data || {};
              setGrandhamData(withoutEmptySelections(savedGrandhamData));
              bindGrandhamFromAPI(savedGrandhamData, 'previous');
              bindGrandhamFromAPI(savedGrandhamData, 'current');
              setCompleted(v.completed || false);
              if (v.pariksha_type) {
                const parikshaTypeArray = Array.isArray(v.pariksha_type) ? v.pariksha_type : [v.pariksha_type];
                setParikshaType(parikshaTypeArray);
                  // Ensure Krishna-specific cascade fields are set BEFORE binding grandhams
                  setKrishnaYajurvedaType(v.krishnaYajurvedaType || '');
                  setKrishnaYajurvedaKramantham(v.krishnaYajurvedaKramantham || '');
                  // File objects can only come from user uploads, not API responses
                  setKrishnaYajurvedaPattaaFile(null);
                  setKrishnaYajurvedaPattaaFileName(v.krishnaYajurvedaPattaaFile_name || '');
                  setKrishnaYajurvedaPattaaFileDownloadLink(v.krishnaYajurvedaPattaaFile_download_link || null);

              } else {
                  setKrishnaYajurvedaType(v.krishnaYajurvedaType || '');
                  setKrishnaYajurvedaKramantham(v.krishnaYajurvedaKramantham || '');
                  // File objects can only come from user uploads, not API responses
                  setKrishnaYajurvedaPattaaFile(null);
                  setKrishnaYajurvedaPattaaFileName(v.krishnaYajurvedaPattaaFile_name || '');
                  setKrishnaYajurvedaPattaaFileDownloadLink(v.krishnaYajurvedaPattaaFile_download_link || null);
              }
              if (v.phone) {
                const phoneStr = String(v.phone || '');
                const digits = phoneStr.replace(/\D/g, '');
                setPhoneLocal(digits.slice(-10));
              }
            }
          } else if (res.status === 409) {
            // Pathasala mismatch - clear aadhar and show error
            const data = await res.json();
            showToast(data.error || 'Vatu registered with different Pathasala', 'error');
            setAadhar('');
          }
          // If not found (404) or unauthenticated (401), do nothing (leave aadhar and form as is)
        } catch (err) {
          // Only show error for network/other errors
          console.error('Error fetching vatu by aadhar:', err);
          showToast('Error loading existing record', 'error');
        } finally {
          setLoading(false);
        }
      } else if (aadhar.length < 12) {
        // Aadhar is not valid (less than 12 digits) - clear all fields except Aadhar
        setVatuName('');
        setDateOfBirth('');
        setDistrict('');
        setGothram('');
        setSutram('');
        setSwasakha('');
        setFatherName('');
        setAddress('');
        setEmail('');
        setMotherName('');
        setMotherMaidenName('');
        setMotherGothram('');
        setGuruvuGariName('');
        // Preserve గురుకులం for anonymous users instead of clearing
        if (!user || user.role?.toLowerCase() === 'anonymous') {
          setPathasala({pathasala_id: gurukulam_patahasala_id, pathasala_name: 'గురుకులం'});
        } else {
          setPathasala({pathasala_id: '', pathasala_name: ''});
        }
        setOtherDetails('');
        setPhoto(null);
        setPhotoConfig({ fit: 'cover', position: 'center' });
        setVatuSignature(null);
        setGuruvuSignature(null);
        // When clearing form, preserve admin signature from pathasala if available (use ref to avoid stale closure)
        setPathasalaSignature(adminSignatureRef.current || null);
        setAadharFile(null);
        setAadharFileName('');
        setAadharFileDownloadLink(null);
        setPhoneLocal('');
        setCompleted(false);
        // Clear all pariksha type grandham states (both current and previous)
        setKrishnaYajurvedaCurrentGrandhams({});
        setLakshanamCurrentKanthastham({});
        setLakshanamCurrentVyakhyanam({});
        setSanskritCurrentGrandhams({});
        setVedadhyayiCurrentGrandhams({});
        setKrishnaYajurvedaPreviousGrandhams({});
        setLakshanamPreviousKanthastham({});
        setLakshanamPreviousVyakhyanam({});
        setSanskritPreviousGrandhams({});
        setVedadhyayiPreviousGrandhams({});
        setGrandhamSelections({});
        // Clear Krishna-specific fields when form is reset
        setKrishnaYajurvedaType('');
        setKrishnaYajurvedaKramantham('');
        setKrishnaYajurvedaPattaaFile(null);
        setKrishnaYajurvedaPattaaFileName('');
        setKrishnaYajurvedaPattaaFileDownloadLink(null);
      }
    };

    const timeoutId = setTimeout(fetchByAadhar, 500); // Debounce
    return () => clearTimeout(timeoutId);
  }, [aadhar, resolvedPathasalaId, lang, user]);

  const handleSave = async () => {
    setSaving(true);
    setLoading(true);
    try {
      const phoneDigits = String(phoneLocal || '').replace(/\D/g, '');
      const parikshaTypeArray = Array.isArray(parikshaType) ? parikshaType : (parikshaType ? [parikshaType] : []);
      
      
      // Create FormData for multipart upload
      const formData = new FormData();
      
      // Add text fields
      formData.append('vatu_name', vatu_name || '');
      formData.append('date_of_birth', date_of_birth || '');
      formData.append('age', calculatedAge || '');
      formData.append('aadhar', aadhar || '');
      formData.append('district', district || '');
      formData.append('gothram', gothram || '');
      formData.append('sutram', sutram || '');
      formData.append('swasakha', swasakha || '');
      formData.append('father_name', father_name || '');
      formData.append('address', address || '');
      formData.append('email', email || '');
      formData.append('mother_name', mother_name || '');
      formData.append('mother_maiden_name', mother_maiden_name || '');
      formData.append('mother_gothram', mother_gothram || '');
      formData.append('guruvu_gari_name', guruvu_gari_name || '');
      formData.append('pathasala_id', resolvedPathasalaId);
      formData.append('other_details', other_details || '');
      formData.append('phone', phoneDigits.length >= 10 ? phoneDigits.slice(-10) : phoneDigits);
      formData.append('pariksha_type', JSON.stringify(parikshaTypeArray));
      formData.append('grandham_data', JSON.stringify(grandhamData));
      formData.append('krishnaYajurvedaType', krishnaYajurvedaType || '');
      formData.append('krishnaYajurvedaKramantham', krishnaYajurvedaKramantham || '');
      formData.append('year', lang === 'telugu' ? teluguYearTelugu : teluguYearSanskrit);
      
      // Add admin-only fields if user is Admin/SuperUser
      if (isAdminOnly) {
        panasalaAntyakshari[teluguYearTelugu] && formData.append('panasala_antyakshari', JSON.stringify(panasalaAntyakshari));
        prantham && formData.append('prantham', prantham);
        
        // Handle attendance_dates: add today's date if checked, remove if unchecked
        const today = new Date().toLocaleDateString('en-CA'); // 'en-CA' locale gives YYYY-MM-DD
        const existingDates = Array.isArray(editVatu?.attendance_dates) ? editVatu.attendance_dates : [];
        
        let updatedDates;
        if (attendance) {
          // If attendance is checked, add today's date to attendance_dates (if not already present)
          updatedDates = Array.from(new Set([...existingDates, today])); // Remove duplicates
        } else {
          // If attendance is unchecked, remove today's date from attendance_dates
          updatedDates = existingDates.filter(date => date !== today);
        }
        
        // Send as JSON array of strings
        updatedDates.length && formData.append('attendance_dates', JSON.stringify(updatedDates));
      }
      
      // Add file fields - only append if files exist
      if (aadharFile) formData.append('aadhar_file', aadharFile);
      if (aadharFileName) formData.append('aadhar_file_name', aadharFileName);
      if (photo) formData.append('photo', photo);
      formData.append('photo_object_config', JSON.stringify(photoConfig));
      if (vatu_signature) formData.append('vatu_signature', vatu_signature);
      if (guruvu_signature) formData.append('guruvu_signature', guruvu_signature);
      if (pathasala_signature) formData.append('pathasala_signature', pathasala_signature);
      if (krishnaYajurvedaPattaaFile) formData.append('krishnaYajurvedaPattaaFile', krishnaYajurvedaPattaaFile);
      if (krishnaYajurvedaPattaaFileName) formData.append('krishnaYajurvedaPattaaFile_name', krishnaYajurvedaPattaaFileName);
      
      console.log('�� Sending FormData with files:', {
        aadhar_file: !!aadharFile,
        photo: !!photo,
        vatu_signature: !!vatu_signature,
        guruvu_signature: !!guruvu_signature,
        pathasala_signature: !!pathasala_signature,
        krishnaYajurvedaPattaaFile: !!krishnaYajurvedaPattaaFile
      });
      
      // Use PUT if editing, POST if creating new
      const isEditing = editVatu && editVatu.vatu_id;
      const endpoint = isEditing ? `/api/vatu/${editVatu.vatu_id}` : '/api/vatu';
      const method = isEditing ? 'PUT' : 'POST';
      console.log(`SAVE: ${method} to ${endpoint}`);
      
      const res = await (method === 'PUT' ? api.put(endpoint, formData) : api.post(endpoint, formData));
      const data = await res.json();
      if (!res.ok) {
        const errorMsg = data?.details ? 
          `Validation errors: ${data.details.map(d => `${d.field}: ${d.message}`).join(', ')}` :
          (data?.error || 'Error saving');
        showToast(errorMsg, 'error');
      } else {
        const msg = data.updated 
          ? (lang === 'telugu' ? 'రికార్డు విజయవంతంగా నవీకరించబడింది!' : 'रेकॉर्ड् सफलतया नवीकृतम्')
          : (lang === 'telugu' ? 'రికార్డు విజయవంతంగా సేవ్ చేయబడింది!' : 'रेकॉर्ड् सफलतया सञ्चितम्');
        showToast(msg, 'success');
        if (onSubmit) onSubmit(data);
      }
    } catch (err) {
      showToast(err.message || 'Network error', 'error');
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };    

  const isSaveDisabled = () => {
    return canAdminEditFiles ? false :
      (saving || completed || !(aadhar && aadhar.length === 12) || (isComplete && isGrandhamComplete) || isReadOnly)
  }

  const handleSubmit = async () => {
    setSubmitting(true);
    setLoading(true);
    try {
      const phoneDigits = String(phoneLocal || '').replace(/\D/g, '');
      const parikshaTypeArray = Array.isArray(parikshaType) ? parikshaType : (parikshaType ? [parikshaType] : []);
      
      
      // Create FormData for multipart upload (same as handleSave)
      const formData = new FormData();
      
      // Add text fields
      formData.append('vatu_name', vatu_name || '');
      formData.append('date_of_birth', date_of_birth || '');
      formData.append('age', calculatedAge || '');
      formData.append('aadhar', aadhar || '');
      formData.append('district', district || '');
      formData.append('gothram', gothram || '');
      formData.append('sutram', sutram || '');
      formData.append('swasakha', swasakha || '');
      formData.append('father_name', father_name || '');
      formData.append('address', address || '');
      formData.append('email', email || '');
      formData.append('mother_name', mother_name || '');
      formData.append('mother_maiden_name', mother_maiden_name || '');
      formData.append('mother_gothram', mother_gothram || '');
      formData.append('guruvu_gari_name', guruvu_gari_name || '');
      formData.append('pathasala_id', resolvedPathasalaId);
      formData.append('other_details', other_details || '');
      formData.append('phone', phoneDigits.length >= 10 ? phoneDigits.slice(-10) : phoneDigits);
      formData.append('pariksha_type', JSON.stringify(parikshaTypeArray));
      formData.append('grandham_data', JSON.stringify(grandhamData));
      formData.append('krishnaYajurvedaType', krishnaYajurvedaType || '');
      formData.append('krishnaYajurvedaKramantham', krishnaYajurvedaKramantham || '');
      formData.append('year', lang === 'telugu' ? teluguYearTelugu : teluguYearSanskrit);
      formData.append('completed', 'true');
      
      // Add file fields - only append if files exist
      if (aadharFile) formData.append('aadhar_file', aadharFile);
      if (aadharFileName) formData.append('aadhar_file_name', aadharFileName);
      if (photo) formData.append('photo', photo);
      formData.append('photo_object_config', JSON.stringify(photoConfig));
      if (vatu_signature) formData.append('vatu_signature', vatu_signature);
      if (guruvu_signature) formData.append('guruvu_signature', guruvu_signature);
      if (pathasala_signature) formData.append('pathasala_signature', pathasala_signature);
      if (krishnaYajurvedaPattaaFile) formData.append('krishnaYajurvedaPattaaFile', krishnaYajurvedaPattaaFile);
      if (krishnaYajurvedaPattaaFileName) formData.append('krishnaYajurvedaPattaaFile_name', krishnaYajurvedaPattaaFileName);
      
      console.log('SUBMIT - Sending FormData with files:', {
        aadhar_file: !!aadharFile,
        photo: !!photo,
        vatu_signature: !!vatu_signature,
        guruvu_signature: !!guruvu_signature,
        pathasala_signature: !!pathasala_signature,
        krishnaYajurvedaPattaaFile: !!krishnaYajurvedaPattaaFile,
        isEditing: !!editVatu
      });
      
      // Use PUT if editing, POST if creating new
      const isEditing = editVatu && editVatu.vatu_id;
      const endpoint = isEditing ? `/api/vatu/${editVatu.vatu_id}` : '/api/vatu';
      const method = isEditing ? 'PUT' : 'POST';
      console.log(`📤 ${method} to ${endpoint}`);
      
      const res = await (method === 'PUT' ? api.put(endpoint, formData) : api.post(endpoint, formData));
      const data = await res.json();
      if (!res.ok) {
        const errorMsg = data?.details ? 
          `Validation errors: ${data.details.map(d => `${d.field}: ${d.message}`).join(', ')}` :
          (data?.error || 'Error submitting');
        showToast(errorMsg, 'error');
      } else {
        const msg = data.updated 
          ? (lang === 'telugu' ? 'రికార్డు విజయవంతంగా నవీకరించబడింది!' : 'रेकॉर्ड् सफलतया नवीकृतम्')
          : (lang === 'telugu' ? 'రికార్డు విజయవంతంగా సృష్టించబడింది!' : 'रेकॉर्ड् सफलतया निर्मितम्');
        showToast(msg, 'success');
        if (onSubmit) onSubmit(data);
        // Show submission success dialog (only for submit action)
        setSubmitSuccessOpen(true);
        
        console.log('🔥 Form submitted successfully. data.updated:', data.updated);
        if (!data.updated) {
          console.log('🧹 Clearing form fields because this was a NEW vatu (not an update)');
          setVatuName(''); setDateOfBirth(''); setCalculatedAge(''); setAadhar(''); setDistrict('');
          setGothram(''); setSutram(''); setSwasakha(''); setFatherName(''); setAddress('');
          setEmail(''); setMotherName(''); setMotherMaidenName(''); setMotherGothram('');
          setGuruvuGariName('');
          // Preserve గురుకులం for anonymous users instead of clearing
          if (!user || user.role?.toLowerCase() === 'anonymous') {
            setPathasala({pathasala_id: gurukulam_patahasala_id, pathasala_name: 'గురుకులం'});
          } else {
            setPathasala({pathasala_id: '', pathasala_name: ''});
          }
          setOtherDetails('');
          setPhoneLocal('');
          setPhoto(null); setVatuSignature(null); setGuruvuSignature(null); setPathasalaSignature(null);
          setParikshaType([]);
          setKrishnaYajurvedaCurrentGrandhams({});
          setLakshanamCurrentKanthastham({});
          setLakshanamCurrentVyakhyanam({});
          setSanskritCurrentGrandhams({});
          setVedadhyayiCurrentGrandhams({});
          setKrishnaYajurvedaPreviousGrandhams({});
          setLakshanamPreviousKanthastham({});
          setLakshanamPreviousVyakhyanam({});
          setSanskritPreviousGrandhams({});
          setVedadhyayiPreviousGrandhams({});
        }
      }
    } catch (err) {
      showToast(err.message || 'Network error', 'error');
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  // Format pariksha dates as "27,28 జనవరి 2026" (Telugu) or Sanskrit equivalent
  const formatParikshaDateString = () => {
    if (!adminConfig?.pariksha_dates || !Array.isArray(adminConfig.pariksha_dates) || adminConfig.pariksha_dates.length === 0) {
      return lang === 'telugu' ? 'పరీక్ష తేదీలు' : 'परीक्षा तिथयः';
    }

    const monthNames = {
      telugu: ['జనవరి', 'ఫిబ్రవరి', 'మార్చి', 'ఏప్రిల్', 'మే', 'జూన్', 'జూలై', 'ఆగస్టు', 'సెప్టెంబర్', 'అక్టోబర్', 'నవంబర్', 'డిసెంబర్'],
      sanskrit: ['जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून', 'जुलाई', 'अगस्त', 'सितंबर', 'अक्टूबर', 'नवंबर', 'दिसंबर']
    };

    const dates = adminConfig.pariksha_dates.map(dateStr => {
      const date = new Date(dateStr);
      return {
        day: date.getDate(),
        month: date.getMonth(),
        year: date.getFullYear()
      };
    });

    // Get unique month-year combination (assuming all dates in same month/year)
    const firstDate = dates[0];
    const monthName = monthNames[lang][firstDate.month];
    const year = firstDate.year;
    
    // Extract just the days
    const days = dates.map(d => d.day).join(',');
    
    return `${days} ${monthName} ${year}`;
  };

  return (
    <>
      {/* Show registrations closed message if deadline has passed */}
      {registrationsClosed && !editVatu && (
        <Box sx={{ 
          p: 4, 
          mb: 3, 
          backgroundColor: '#fff3e0', 
          border: '2px solid #ff9800', 
          borderRadius: 2,
          textAlign: 'center'
        }}>
          <Typography variant="h5" sx={{ color: '#e65100', fontWeight: 600, mb: 2 }}>
            {lang === 'telugu' ? '📋 నమోదుకరణ ముగిసిపోయింది' : '📋 पंजीकरण समाप्त हो गया'}
          </Typography>
          <Typography variant="body1" sx={{ color: '#e65100' }}>
            {lang === 'telugu' 
              ? `విశ్వావసు సంవత్సర పరీక్ష నమోదు ప్రక్రియ గడువు ముగిసింది. ఇంకా ఆసక్తి ఉన్నవారు గోటేటి కళ్యాణమంటపం (నాగోలు)కి పరీక్ష జరుగు ${formatParikshaDateString()} తేదీలలో వచ్చి ప్రత్యక్షంగా నమోదు చేసుకోగలరు.`
              : `विश्वावसु वर्ष परीक्षा पञ्जीकरण प्रक्रिया समाप्ति तिथि पारितम्। यदि इच्छुकाः सन्ति तर्हि गोटेति कल्याणमन्तपं नागोलु प्रति परीक्षा दिनाङ्केषु ${formatParikshaDateString()} आगत्य प्रत्यक्षं पञ्जीकरणं कर्तुं शक्नुवन्ति।`}
          </Typography>
        </Box>
      )}
      
      {/* Show main form only if registrations are open or if editing existing vatu */}
      {(!registrationsClosed || editVatu) && (
      <>
      {/* Transform button removed — transform now runs on language flip */}
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{ mb: 2 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: (theme) => theme.palette.grey[50],
            borderLeft: isComplete
              ? (theme) => `4px solid ${theme.palette.success.main}`
              : (theme) => `4px solid ${theme.palette.error.main}`,
            "&:hover": {
              backgroundColor: (theme) => theme.palette.action.hover,
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {isComplete ? (
              <CheckCircleOutlineIcon sx={{ color: "success.main" }} />
            ) : (
              <HourglassEmptyIcon sx={{ color: "error.main" }} />
            )}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {vatuAccordionLabels[lang].title} {isComplete ? `(${vatuAccordionLabels[lang].complete})` : `(${vatuAccordionLabels[lang].pending})`}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {isComplete
                  ? vatuAccordionLabels[lang].allFieldsCompleted
                  : vatuAccordionLabels[lang].fillAllFields}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ maxWidth: 900, mx: "auto", p: 1 }}>
            {/* Read-only mode notice */}
            {isReadOnly && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: '#e3f2fd',
                  border: '2px solid #2196f3',
                  borderRadius: 1,
                  textAlign: 'center'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  {lang === 'telugu' ? '📖 చూడడానికి  మాత్రమే - సవరణ సాధ్యం కాదు' : '📖 केवल पठनीयः - संपादन संभव नहीं'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#1565c0' }}>
                  {lang === 'telugu' ? 'మీరు ఈ రికార్డ్‌ను చూడవచ్చు మరియు ఆధార్ మరియు పట్ట ఫైలులను డౌన్‌లోడ్ చేయవచ్చు' : 'आप इस रेकॉर्डं को देखितुम् शक्नुवन्तः तथा आधार पत्रं पट्टपत्रं च अवतारयितुम् शक्नुवन्तः'}
                </Typography>
              </Box>
            )}
            
            {/* Photo upload - prominently displayed at top */}
            <Box
              sx={{
                mb: 3,
                pb: 3,
                borderBottom: "2px solid",
                borderColor: "grey.200",
                textAlign: "center",
              }}
            >
              <PhotoUpload
                label={lang === "telugu" ? "విద్యార్థి ఫోటో" : "वटुः फोटो"}
                value={photo}
                onChange={setPhoto}
                size={120}
                required
                disabled={isReadOnly && !canAdminEditFiles}
                objectConfig={photoConfig}
              />
              
              {/* Collapsible Photo Adjustment Section */}
              {photo && (
                <Accordion sx={{ mt: 2, border: '1px solid #ddd', borderRadius: '8px' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#f8f9fa', '&:hover': { backgroundColor: '#f0f1f2' } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#7f4b2d' }}>
                      {lang === 'telugu' ? 'ఫోటో సర్దుబాటు చేయండి' : 'फोटो समायोजित करें'}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2, backgroundColor: '#fff8ed' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Position Grid */}
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#7f4b2d', mb: 1 }}>
                          {lang === 'telugu' ? 'స్థానం' : 'स्थिति'}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
                          {['top left', 'top', 'top right', 'left', 'center', 'right', 'bottom left', 'bottom', 'bottom right'].map((pos) => (
                            <Box
                              key={pos}
                              onClick={() => setPhotoConfig({ ...photoConfig, position: pos })}
                              sx={{
                                width: '40px',
                                height: '40px',
                                border: '2px solid',
                                borderColor: photoConfig.position === pos ? '#2e7d32' : '#ddd',
                                backgroundColor: photoConfig.position === pos ? '#e8f5e9' : '#fff',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '16px',
                                color: photoConfig.position === pos ? '#2e7d32' : '#999',
                                fontWeight: 600,
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#2e7d32',
                                  backgroundColor: '#f0f8f5',
                                  transform: 'scale(1.05)'
                                }
                              }}
                              title={pos}
                            >
                              {pos === 'center' && '●'}
                              {pos === 'top left' && '↖'}
                              {pos === 'top' && '↑'}
                              {pos === 'top right' && '↗'}
                              {pos === 'left' && '←'}
                              {pos === 'right' && '→'}
                              {pos === 'bottom left' && '↙'}
                              {pos === 'bottom' && '↓'}
                              {pos === 'bottom right' && '↘'}
                            </Box>
                          ))}
                        </Box>
                      </Box>

                      {/* Fit Options - 2x2 Grid */}
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#7f4b2d', mb: 1 }}>
                          {lang === 'telugu' ? 'ఫిట్ విధానం' : 'फिट विधि'}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                          {[
                            { value: 'cover', label: lang === 'telugu' ? 'కవర్' : 'कवर', desc: lang === 'telugu' ? 'కత్తిరించు' : 'काटना', icon: '📐' },
                            { value: 'contain', label: lang === 'telugu' ? 'కంటైన్' : 'कंटेन', desc: lang === 'telugu' ? 'పూర్ణ చిత్రం' : 'पूरी छवि', icon: '📦' },
                            { value: 'fill', label: lang === 'telugu' ? 'ఫిల్' : 'भरें', desc: lang === 'telugu' ? 'విస్తరించు' : 'फैलाना', icon: '📏' },
                            { value: 'scale-down', label: lang === 'telugu' ? 'స్కేల్' : 'स्केल', desc: lang === 'telugu' ? 'చిన్న విధానం' : 'छोटा तरीका', icon: '🔽' }
                          ].map((fitOption) => (
                            <Box
                              key={fitOption.value}
                              onClick={() => setPhotoConfig({ ...photoConfig, fit: fitOption.value })}
                              sx={{
                                p: 1.2,
                                border: '2px solid',
                                borderColor: photoConfig.fit === fitOption.value ? '#2e7d32' : '#ddd',
                                backgroundColor: photoConfig.fit === fitOption.value ? '#e8f5e9' : '#fff',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#2e7d32',
                                  backgroundColor: '#f0f8f5',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 2px 8px rgba(46, 125, 50, 0.15)'
                                }
                              }}
                            >
                              <Typography sx={{ fontSize: '20px', mb: 0.5, textAlign: 'center' }}>
                                {fitOption.icon}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  display: 'block', 
                                  fontWeight: 600, 
                                  color: photoConfig.fit === fitOption.value ? '#2e7d32' : '#333',
                                  textAlign: 'center'
                                }}
                              >
                                {fitOption.label}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  display: 'block', 
                                  color: '#999',
                                  fontSize: '10px',
                                  textAlign: 'center',
                                  mt: 0.3
                                }}
                              >
                                {fitOption.desc}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                rowGap: 2,
                columnGap: 2,
              }}
            >
              <Box>
                <TextField
                  label={lang === "telugu" ? "ఆధార్" : "आधार"}
                  value={aadhar}
                    onChange={(e) => {
                      const digits = String(e.target.value || '').replace(/\D/g, '');
                      setAadhar(digits);
                    }}
                  placeholder={
                    lang === "telugu"
                      ? "ఆధార్ సంఖ్యను నమోదుచేయండి"
                      : "आधारसंख्यां लिखतु"
                  }
                    inputProps={{ maxLength: 12, inputMode: 'numeric', pattern: '\\d*' }}
                  error={!!validateAadhar(aadhar)}
                  helperText={validateAadhar(aadhar)}
                  fullWidth
                  disabled={isReadOnly && !canAdminEditFiles}
                />
              </Box>
              <Box>
                <FormControl fullWidth required error={!aadharFile && !isReadOnly}>
                  <Button
                    variant={aadharFile ? "contained" : "outlined"}
                    component="label"
                    fullWidth
                    disabled={isReadOnly && !canAdminEditFiles}
                    sx={{ py: 1.2, textAlign: 'center', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: aadharFile ? 'primary.contrastText' : 'text.primary' }}
                  >
                    {aadharFile ?
                      (lang === 'telugu' ? '✓ ఆధార్ డాక్యుమెంట్ ఎంచుకోబడింది' : '✓ आधार दस्तावेज़ चुना गया')
                      :
                      (lang === 'telugu' ? 'ఆధార్ కార్డు అప్లోడ్ చేయండి *' : 'आधार पत्र अपलोड करें *')
                    }
                    <input
                      type="file"
                      hidden
                      accept="application/pdf,image/*"
                      onChange={(e) => {
                        const file = e.target.files && e.target.files[0];
                        if (file) {
                          // Store the File object directly
                          setAadharFile(file);
                          setAadharFileName(file.name || '');
                          setAadharFileDownloadLink(null); // Clear download link when new file selected
                        }
                      }}
                    />
                  </Button>
                  {!aadharFile && (
                    <Typography variant="caption" sx={{ color: 'primary.main', mt: 0.5 }}>
                      {lang === 'telugu' ? 'పాస్వర్డ్ లేని ఆధార్ కార్డుని మాత్రమే అప్లోడ్ చేయండి' : 'गोपनीयाङ्करहितम् आधारकार्डम् एव अपलोड् कुर्वन्तु'}
                    </Typography>
                  )}
                  {aadharFileName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                      {aadharFileDownloadLink ? (
                        <Button 
                          size="small"
                          sx={{ textDecoration: 'none', color: '#1976d2', fontSize: '0.875rem', fontWeight: 500, textTransform: 'none' }}
                          onClick={async () => {
                            try {
                              const response = await fetch(aadharFileDownloadLink);
                              if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                              
                              const blob = await response.blob();
                              console.log('📥 Downloaded blob:', { type: blob.type, size: blob.size });
                              
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = aadharFileName || `aadhar_${new Date().getTime()}`;
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);
                            } catch (error) {
                              console.error('❌ Download error:', error);
                              alert(`Download failed: ${error.message}`);
                            }
                          }}
                        >
                          {aadharFileName}
                        </Button>
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{aadharFileName}</Typography>
                      )}
                      <Button size="small" onClick={() => { setAadharFile(null); setAadharFileName(''); setAadharFileDownloadLink(null); }} disabled={isReadOnly}>
                        {lang === 'telugu' ? 'తొలగించు' : 'हटाएं'}
                      </Button>
                    </Box>
                  )}
                </FormControl>
              </Box>
              <Box>
                <LocalizedInput
                  value={vatu_name}
                  setValue={setVatuName}
                  labels={{ telugu: "విద్యార్థి పూర్తి పేరు", sanskrit: "उपनाम" }}
                  placeholders={{
                    telugu: "విద్యార్థి పూర్తి పేరు (ఇంటి పేరుతో సహా) నమోదుచేయండి",
                    sanskrit: "उपनाम लिखतु",
                  }}
                  lang={lang}
                  disabled={isReadOnly}
                />
              </Box>

              <Box>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                  <DatePicker
                    label={lang === "telugu" ? "పుట్టిన తేది" : "जन्मतिथिः"}
                    // Parse stored DD/MM/YYYY explicitly so MM/DD/YYYY is not accepted
                    value={(() => {
                      if (!date_of_birth) return null;
                      if (date_of_birth.includes('/')) {
                        const [d, m, y] = date_of_birth.split('/');
                        const day = parseInt(d, 10);
                        const month = parseInt(m, 10) - 1;
                        const year = parseInt(y, 10);
                        return new Date(year, month, day);
                      }
                      return new Date(date_of_birth);
                    })()}
                    inputFormat="dd/MM/yyyy"
                    mask="__/__/____"
                    onChange={(newValue) => {
                      if (newValue) {
                        const day = String(newValue.getDate()).padStart(2, "0");
                        const month = String(newValue.getMonth() + 1).padStart(
                          2,
                          "0"
                        );
                        const year = newValue.getFullYear();
                        setDateOfBirth(`${day}/${month}/${year}`);
                      } else {
                        setDateOfBirth("");
                      }
                    }}
                    maxDate={new Date()}
                    views={["year", "month", "day"]}
                    openTo="year"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        disabled: isReadOnly,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
                {calculatedAge !== "" && (
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.5,
                      display: "block",
                      color: "text.secondary",
                      fontWeight: 500,
                    }}
                  >
                    {lang === "telugu"
                      ? `వయస్సు: ${calculatedAge} సంవత్సరాలు`
                      : `आयुः: ${calculatedAge} वर्षाणि`}
                  </Typography>
                )}
              </Box>

              <Box>
                <LocalizedInput
                  value={district}
                  setValue={setDistrict}
                  labels={{ telugu: "జిల్లా", sanskrit: "जिला" }}
                  placeholders={{
                    telugu: "జిల్లా నమోదుచేయండి",
                    sanskrit: "जिलां लिखतु",
                  }}
                  lang={lang}
                  disabled={isReadOnly}
                />
              </Box>
              <Box>
                <LocalizedInput
                  value={gothram}
                  setValue={setGothram}
                  labels={{ telugu: "గోత్రము", sanskrit: "गोत्रम्" }}
                  placeholders={{
                    telugu: "గోత్రము నమోదుచేయండి",
                    sanskrit: "गोत्रम् लिखतु",
                  }}
                  lang={lang}
                  disabled={isReadOnly}
                />
              </Box>

              <Box>
                <LocalizedInput
                  value={sutram}
                  setValue={setSutram}
                  labels={{ telugu: "సూత్రము", sanskrit: "सूत्रम्" }}
                  placeholders={{
                    telugu: "సూత్రము నమోదుచేయండి",
                    sanskrit: "सूत्रम् लिखतु",
                  }}
                  lang={lang}
                  disabled={isReadOnly}
                />
              </Box>
              <Box>
                <LocalizedInput
                  value={swasakha}
                  setValue={setSwasakha}
                  labels={{ telugu: "స్వశాఖ", sanskrit: "स्वशाखा" }}
                  placeholders={{
                    telugu: "స్వశాఖ నమోదుచేయండి",
                    sanskrit: "स्वशाखां लिखतु",
                  }}
                  lang={lang}
                  disabled={isReadOnly}
                />
              </Box>

              <Box>
                <LocalizedInput
                  value={father_name}
                  setValue={setFatherName}
                  labels={{
                    telugu: "తండ్రి గారి పేరు (పూర్తిగా)",
                    sanskrit: "पितृनाम (पूर्णम्)",
                  }}
                  placeholders={{
                    telugu: "తండ్రి గారి పూర్తి పేరు నమోదుచేయండి",
                    sanskrit: "पितृनाम पूर्णम् लिखतु",
                  }}
                  lang={lang}
                  disabled={isReadOnly}
                />
              </Box>
              <Box>
                <LocalizedInput
                  value={address}
                  setValue={setAddress}
                  labels={{ telugu: "చిరునామా", sanskrit: "पता" }}
                  placeholders={{
                    telugu: "చిరునామా నమోదుచేయండి",
                    sanskrit: "पतां लिखतु",
                  }}
                  lang={lang}
                  disabled={isReadOnly}
                />
              </Box>

              <Box>
                <TextField
                  label={lang === "telugu" ? "ఇ-మెయిల్" : "ईमेल"}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={
                    lang === "telugu" ? "ఇ-మెయిల్ నమోదుచేయండి" : "ईमेलम् लिखतु"
                  }
                  error={!!validateEmail(email)}
                  helperText={validateEmail(email)}
                  fullWidth
                  disabled={isReadOnly}
                />
              </Box>
              <Box>
                <LocalizedInput
                  value={mother_name}
                  setValue={setMotherName}
                  labels={{ telugu: "తల్లి గారి పేరు", sanskrit: "मातृनाम" }}
                  placeholders={{
                    telugu: "తల్లి గారి పేరు నమోదుచేయండి",
                    sanskrit: "मातृनाम लिखतु",
                  }}
                  lang={lang}
                  disabled={isReadOnly}
                />
              </Box>
              <Box>
                <LocalizedInput
                  value={mother_gothram}
                  setValue={setMotherGothram}
                  labels={{
                    telugu: "తల్లి గారి పుట్టింటి గోత్రము",
                    sanskrit: "मातृ गोत्रम्",
                  }}
                  placeholders={{
                    telugu: "తల్లి గారి పుట్టింటి గోత్రము నమోదుచేయండి",
                    sanskrit: "मातृ गोत्रम् लिखतु",
                  }}
                  lang={lang}
                  disabled={isReadOnly}
                />
              </Box>
              <Box>
                <LocalizedInput
                  value={mother_maiden_name}
                  setValue={setMotherMaidenName}
                  labels={{
                    telugu: "తల్లి గారి పుట్టింటి పేరు",
                    sanskrit: "मातृ जन्मनाम",
                  }}
                  placeholders={{
                    telugu: "తల్లి గారి పుట్టింటి పేరు నమోదుచేయండి",
                    sanskrit: "मातृ जन्मनाम लिखतु",
                  }}
                  lang={lang}
                  disabled={isReadOnly}
                />
              </Box>

              {/* Country selector removed — phone uses +91 prefix by default */}
              <Box>
                <TextField
                  label={lang === 'telugu' ? 'ఫోన్ (లోకల్)' : 'Phone (local)'}
                  value={phoneLocal}
                  onChange={(e) => setPhoneLocal(e.target.value)}
                  placeholder="9876543210"
                  inputProps={{ maxLength: 15 }}
                  error={!!validatePhone(phoneLocal)}
                  helperText={validatePhone(phoneLocal)}
                  fullWidth
                  disabled={isReadOnly}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">+91</InputAdornment>
                  }}
                />
              </Box>
              <Box>
                {user && user.role === 'PathashalaAdmin' ? (
                  <Box sx={{ position: 'relative' }}>
                    <LocalizedInput
                      value={guruvu_gari_name}
                      setValue={setGuruvuGariName}
                      labels={{
                        telugu: "గురువుగారి పేరు (ఇంటి పేరుతో సహా)",
                        sanskrit: "गुरोः नाम (उपनामसहितम्)",
                      }}
                      placeholders={{
                        telugu: "గురువుగారి పూర్తి పేరు నమోదుచేయండి",
                        sanskrit: "गुरोः पूर्णनाम लिखतु",
                      }}
                      lang={lang}
                      onFocus={() => Object.keys(guruvuGaruNames).length > 0 && setShowGuruvuSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowGuruvuSuggestions(false), 200)}
                      disabled={isReadOnly}
                    />
                    {/* Suggestions dropdown */}
                    {showGuruvuSuggestions && Object.keys(guruvuGaruNames).length > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          backgroundColor: 'white',
                          border: '1px solid',
                          borderColor: 'grey.300',
                          borderRadius: 1,
                          boxShadow: 2,
                          maxHeight: 200,
                          overflow: 'auto'
                        }}
                      >
                        {Object.keys(guruvuGaruNames)
                          .filter(name => 
                            !guruvu_gari_name || 
                            name.toLowerCase().includes(guruvu_gari_name.toLowerCase())
                          )
                          .map(name => (
                            <Box
                              key={name}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1,
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'grey.100' }
                              }}
                            >
                              <Typography
                                onClick={() => handleSelectGuruvuSuggestion(name)}
                                sx={{ flex: 1 }}
                              >
                                {name}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setGuruvuToDelete(name);
                                  setShowDeleteGuruvuDialog(true);
                                }}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))
                        }
                      </Box>
                    )}
                  </Box>
                ) : (
                  <LocalizedInput
                    value={guruvu_gari_name}
                    setValue={setGuruvuGariName}
                    labels={{
                      telugu: "గురువుగారి పేరు (ఇంటి పేరుతో సహా)",
                      sanskrit: "गुरोः नाम (उपनामसहितम्)",
                    }}
                    placeholders={{
                      telugu: "గురువుగారి పూర్తి పేరు నమోదుచేయండి",
                      sanskrit: "गुरोः पूर्णनाम लिखतु",
                    }}
                    lang={lang}
                    disabled={isReadOnly}
                  />
                )}
              </Box>

              <Box>
                {(user && user?.role?.toLowerCase() === 'anonymous') || !user ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, backgroundColor: 'rgba(33, 150, 243, 0.1)', borderRadius: 1 }}>
                    <Typography sx={{ fontWeight: 500 }}>
                      {lang === 'telugu' ? 'గురుకుల విద్యార్థి' : 'गुरुकुल विद्यार्थी'}
                    </Typography>
                    <InfoIcon 
                      sx={{ cursor: 'pointer', color: 'primary.main' }}
                      onClick={() => setShowGurukshaDialog(true)}
                    />
                  </Box>
                ) : user && user.role === 'PathashalaAdmin' ? (
                  <FormControl fullWidth>
                    <InputLabel>
                      {lang === 'telugu' ? 'పాఠశాల పేరు' : 'पाठशालानाम'}
                    </InputLabel>
                    <Select
                      value={pathasala?.pathasala_id || ''}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedPathasala = pathasalaList.find(p => p.pathasala_id === selectedId);
                        if (selectedPathasala) {
                          setPathasala({pathasala_id: selectedPathasala.pathasala_id, pathasala_name: selectedPathasala.pathasala_name});
                          console.log('Selected pathasala:', selectedPathasala);
                        }
                      }}
                      label={lang === 'telugu' ? 'పాఠశాల పేరు' : 'पाठशालानाम'}
                    >
                      {pathasalaList.map((pathasala) => (
                        <MenuItem key={pathasala.pathasala_id} value={pathasala.pathasala_id}>
                          {pathasala.pathasala_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <LocalizedInput
                    value={pathasala.pathasala_name}
                    setValue={setPathasala}
                    labels={{ telugu: "పాఠశాల పేరు", sanskrit: "पाठशालानाम" }}
                    disabled={isReadOnly}
                    placeholders={{
                      telugu: "పాఠశాల పేరు నమోదుచేయండి",
                      sanskrit: "पाठशालानाम लिखतु",
                    }}
                    lang={lang}
                  />
                )}
              </Box>
              
              {/* Pariksha Type Selection */}
              <Box>
                <FormControl fullWidth required>
                  <InputLabel>
                    {lang === 'telugu' ? 'పరీక్ష వివరాలు' : 'परीक्षा विवरणानि'}
                  </InputLabel>
                  <Select
                    multiple
                    value={parikshaType}
                    disabled={isReadOnly}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      const wasKrishnaSelected = parikshaType.includes('krishna_yajurveda');
                      const isKrishnaSelected = newValue.includes('krishna_yajurveda');
                      
                      setParikshaType(newValue);
                      setGrandhamSelections((previous) => {
                        const next = {};
                        Object.entries(previous).forEach(([type, selections]) => {
                          if (newValue.includes(type)) {
                            next[type] = selections;
                            return;
                          }
                          if (selections.previous && Object.keys(selections.previous).length > 0) {
                            next[type] = { previous: selections.previous };
                          }
                        });
                        return next;
                      });
                      setGrandhamData((previous) =>
                        Object.fromEntries(
                          Object.entries(previous).filter(([type]) => newValue.includes(type))
                        )
                      );

                      // Clear current selections for every pariksha type that was removed.
                      parikshaType
                        .filter((type) => !newValue.includes(type))
                        .forEach((type) => {
                          if (type === 'krishna_yajurveda') setKrishnaYajurvedaCurrentGrandhams({});
                          if (type === 'lakshanam') {
                            setLakshanamCurrentKanthastham({});
                            setLakshanamCurrentVyakhyanam({});
                          }
                          if (type === 'sanskrit') setSanskritCurrentGrandhams({});
                          if (type === 'vedadhyayi_sahityam') setVedadhyayiCurrentGrandhams({});
                        });
                      
                      // Always clear Krishna grandhams when krishna_yajurveda selection changes
                      if (wasKrishnaSelected !== isKrishnaSelected) {
                        setKrishnaYajurvedaCurrentGrandhams({});
                      }
                      
                      // Reset Krishna Yajurvedam fields if deselected
                      if (!newValue.includes('krishna_yajurveda')) {
                        setKrishnaYajurvedaType('');
                        setKrishnaYajurvedaKramantham('');
                        setKrishnaYajurvedaPattaaFile(null);
                      }
                    }}
                    label={lang === 'telugu' ? 'పరీక్ష వివరాలు' : 'परीक्षा विवरणानि'}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          '& .MuiList-root': {
                            display: 'grid',
                            gap: 0.5,
                            p: 1
                          }
                        }
                      }
                    }}
                    renderValue={(selected) => 
                      selected.map(val => 
                        parikshaTypeOptions.find(opt => opt.value === val)?.label[lang]
                      ).join(', ')
                    }
                    >
                    {parikshaTypeOptions.map((option) => {
                      // Age-based disabling for entire pariksha types
                      let optionDisabled = false;
                      if (option.value === 'sanskrit' && ageNum > 30) optionDisabled = true; // Samskrutam limit 30
                      if (option.value === 'lakshanam' && ageNum > 40) optionDisabled = true; // Lakshanam limit 40
                      if (option.value === 'krishna_yajurveda' && ageNum > 35) optionDisabled = true; // Krishna entirely disabled beyond 35
                      if (option.value === 'vedadhyayi_sahityam' && ageNum > 30) optionDisabled = true; // Vedadhyai Saahityam limit 30

                      return (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          disabled={optionDisabled}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '40px minmax(0, 1fr)',
                            alignItems: 'center',
                            minHeight: 48,
                            px: 1,
                            borderRadius: 1
                          }}
                        >
                          <Checkbox
                            checked={parikshaType.indexOf(option.value) > -1}
                            sx={{ p: 0.5 }}
                          />
                          <Typography component="span" sx={{ lineHeight: 1.35 }}>
                            {option.label[lang]}
                          </Typography>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>

              {/* Krishna Yajurvedam - Type Selection (Varshika or Pattaa) */}
              {parikshaType.includes('krishna_yajurveda') && (
                <Box>
                  <FormControl fullWidth>
                    <InputLabel>
                      {lang === 'telugu' ? 'కృష్ణ యజుర్వేదం - పరీక్ష విధానం' : 'कृष्ण यजुर्वेदम् - परीक्षा प्रकारः'}
                    </InputLabel>
                    <Select
                      value={krishnaYajurvedaType}
                      disabled={isReadOnly}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setKrishnaYajurvedaType(newType);
                        setKrishnaYajurvedaKramantham('');
                        setKrishnaYajurvedaPattaaFile(null);
                        
                        // Always clear all grandhams when krishnaYajurvedaType changes
                        setKrishnaYajurvedaCurrentGrandhams({});
                      }}
                      label={lang === 'telugu' ? 'కృష్ణ యజుర్వేదం - పరీక్ష విధానం' : 'कृष्ण यजुर्वेदम् - परीक्षा प्रकारः'}
                    >
                      <MenuItem value="first_varshika_pariksha">
                        {lang === 'telugu' ? 'వార్షిక పరీక్ష' : 'वार्षिक परीक्षा'}
                      </MenuItem>
                      <MenuItem value="pattaa_pariksha">
                        {lang === 'telugu' ? 'పట్టా పరీక్ష' : 'पट्टा परीक्षा'}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}

              {/* Krishna Yajurvedam - Kramantham Type Selection (Only for Pattaa Pariksha) */}
              {parikshaType.includes('krishna_yajurveda') && krishnaYajurvedaType === 'pattaa_pariksha' && (
                <Box>
                  <FormControl fullWidth>
                    <InputLabel>
                      {lang === 'telugu' ? 'పట్టా పరీక్ష విధానం' : 'क्रमन्थम् प्रकारः'}
                    </InputLabel>
                    <Select
                            value={krishnaYajurvedaKramantham}
                            disabled={isReadOnly}
                            onChange={(e) => {
                              const val = e.target.value;
                              // If special options selected, open attestation dialog first
                              if (val === 'vishesha_kramantham' || val === 'vishesha_ghana') {
                                setPendingKramantham(val);
                                setAttestationChecked(false);
                                setShowKramanthamDialog(true);
                              } else {
                                setKrishnaYajurvedaKramantham(val);
                                setKrishnaYajurvedaPattaaFile(null);
                                // Clear grandhams so cascade can run fresh with new kramantham selection
                                setKrishnaYajurvedaCurrentGrandhams({});
                              }
                            }}
                      label={lang === 'telugu' ? 'పట్టా పరీక్ష విధానం' : 'क्रमन्थम् प्रकारः'}
                    >
                      <MenuItem value="vishesha_kramantham">
                        {lang === 'telugu' ? 'విశేష క్రమాంతం' : 'विशेष क्रमन्थम्'}
                      </MenuItem>
                      <MenuItem value="saamaanya_kramantham">
                        {lang === 'telugu' ? 'సామాన్య క్రమాంతం' : 'सामान्य क्रमन्थम्'}
                      </MenuItem>
                      <MenuItem value="vishesha_ghana">
                        {lang === 'telugu' ? 'విశేష ఘనం' : 'विशेष घनम्'}
                      </MenuItem>
                      <MenuItem value="saamaanya_ghana">
                        {lang === 'telugu' ? 'సామాన్య ఘనం' : 'सामान्य घनम्'}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}

              {/* File Upload for Vishesha Ghana */}
              {parikshaType.includes('krishna_yajurveda') && krishnaYajurvedaType === 'pattaa_pariksha' && krishnaYajurvedaKramantham === 'vishesha_ghana' && (
                <Box>
                  <FormControl fullWidth>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      disabled={isReadOnly && !canAdminEditFiles}
                      sx={{ py: 1.2, textAlign: 'center', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.primary' }}
                  >
                      {krishnaYajurvedaPattaaFile ? 
                        (lang === 'telugu' ? '✓ వేదారణ్యం క్రమాంతం పట్టా ఎంచుకోబడింది' : '✓ वेदारण्यम् क्रमन्थम् पट्टा चयनित') 
                        : 
                        (lang === 'telugu' ? 'వేదారణ్యం క్రమాంతం పట్టా ఎంచుకోండి' : 'वेदारण्यम् क्रमन्थम् पट्टा चुनें')
                      }
                      <input
                        type="file"
                        hidden
                        accept="application/pdf,image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // Store the File object directly
                            setKrishnaYajurvedaPattaaFile(file);
                            setKrishnaYajurvedaPattaaFileName(file.name || '');
                            setKrishnaYajurvedaPattaaFileDownloadLink(null); // Clear download link when new file selected
                          }
                        }}
                      />
                    </Button>
                  </FormControl>
                  {krishnaYajurvedaPattaaFileName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                      {krishnaYajurvedaPattaaFileDownloadLink ? (
                        <Button 
                          size="small"
                          sx={{ textDecoration: 'none', color: '#1976d2', fontSize: '0.875rem', fontWeight: 500, textTransform: 'none' }}
                          onClick={async () => {
                            try {
                              const response = await fetch(krishnaYajurvedaPattaaFileDownloadLink);
                              if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                              
                              const blob = await response.blob();
                              console.log('📥 Downloaded blob:', { type: blob.type, size: blob.size });
                              
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = krishnaYajurvedaPattaaFileName || `pattaa_${new Date().getTime()}`;
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);
                            } catch (error) {
                              console.error('❌ Download error:', error);
                              alert(`Download failed: ${error.message}`);
                            }
                          }}
                        >
                          {krishnaYajurvedaPattaaFileName}
                        </Button>
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{krishnaYajurvedaPattaaFileName}</Typography>
                      )}
                      <Button size="small" disabled={isReadOnly} onClick={() => { setKrishnaYajurvedaPattaaFile(null); setKrishnaYajurvedaPattaaFileName(''); setKrishnaYajurvedaPattaaFileDownloadLink(null); }}>
                        {lang === 'telugu' ? 'తొలగించు' : 'हटाएं'}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
              
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Admin-Only Optional Fields Accordion */}
      {isAdminOnly && showAdminFieldsToday && (
        <Accordion 
          sx={{ 
            mb: 2, 
            backgroundColor: '#fff3e0',
            borderLeft: isAdminAccordionComplete
              ? (theme) => `4px solid ${theme.palette.success.main}`
              : (theme) => `4px solid #ff9800`,
            "&:hover": {
              backgroundColor: (theme) => theme.palette.action.hover,
            },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {isAdminAccordionComplete ? (
                <CheckCircleOutlineIcon sx={{ color: "success.main" }} />
              ) : (
                <HourglassEmptyIcon sx={{ color: "#ff9800" }} />
              )}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#e65100' }}>
                  {lang === 'telugu' ? '⚙️ పరీక్ష హాజరు మరియు పనసల అంత్యాక్షరి స్థితి' : '⚙️ परीक्षा उपस्थितिः च पनसल अन्त्याक्षरीय स्थिति'} {isAdminAccordionComplete ? `(${vatuAccordionLabels[lang].complete})` : `(${vatuAccordionLabels[lang].pending})`}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {isAdminAccordionComplete
                    ? vatuAccordionLabels[lang].allFieldsCompleted
                    : vatuAccordionLabels[lang].fillAllFields}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ maxWidth: 900, mx: 'auto', p: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Attendance Checkbox */}
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={attendance}
                      onChange={(e) => setAttendance(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {lang === 'telugu' ? '✓ విద్యార్థి హాజరయ్యారా ప్రత్యక్షంగా' : '✓ विद्यार्थी प्रत्यक्षं उपस्थितः किम्'}
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              {/* Panasala Antyakshari Selection */}
              <Box>
                <FormControl fullWidth>
                  <InputLabel>
                    {lang === 'telugu' ? 'పనసల అంత్యాక్షరి స్థితి' : 'पानसाल अन्त्याक्षरी स्थितिः'}
                  </InputLabel>
                  <Select
                    value={panasalaAntyakshari[teluguYearTelugu] || ''}
                    onChange={(e) => setPanasalaAntyakshari({ [teluguYearTelugu]: e.target.value })}
                    label={lang === 'telugu' ? 'పనసల అంత్యాక్షరి స్థితి' : 'पानसाल अन्त्याक्षरी स्थितिः'}
                  >
                    <MenuItem value="">
                      <em>{lang === 'telugu' ? 'ఎంచుకోండి' : 'निर्वचनम् करतु'}</em>
                    </MenuItem>
                    <MenuItem 
                      value="junior"
                      disabled={calculateTotalPannams() > 20}
                    >
                      {lang === 'telugu' ? '🎓 జూనియర్' : '🎓 जूनियरः'}
                    </MenuItem>
                    <MenuItem value="senior">
                      {lang === 'telugu' ? '🏆 సీనియర్' : '🏆 वरिष्ठः'}
                    </MenuItem>
                    <MenuItem value="not_interested">
                      {lang === 'telugu' ? '❌ ఆసక్తి లేదు' : '❌ न चेष्टितुम् इच्छति'}
                    </MenuItem>
                  </Select>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                    {lang === 'telugu' 
                      ? 'వటువుకు పనసల అంత్యాక్షరిలో పాల్గొనే ఆసక్తిని గుర్తించండి'
                      : 'वटुः पानसाल अन्त्याक्षरीयां भागग्रहणे चेतनां दर्शयतु'}
                  </Typography>
                </FormControl>
              </Box>

              {/* Travel Expenses Selection */}
              <Box>
                <FormControl fullWidth>
                  <InputLabel>
                    {lang === 'telugu' ? 'ప్రాంతం' : 'प्रदेशः'}
                  </InputLabel>
                  <Select
                    value={prantham || ''}
                    onChange={(e) => setPrantham(e.target.value)}
                    label={lang === 'telugu' ? 'ప్రాంతం' : 'प्रदेशः'}
                  >
                    <MenuItem value="">
                      <em>{lang === 'telugu' ? 'ఎంచుకోండి' : 'निर्वचनम् करतु'}</em>
                    </MenuItem>
                    {console.log('🗺️ Available travel expenses:', travelExpenses)}
                    {travelExpenses.map((expense) => (
                      <MenuItem key={expense._id} value={expense.expenseDetail}>
                        {expense.expenseDetail}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                    {lang === 'telugu' 
                      ? 'వటువు ప్రాంతమును ఎంచుకోండి'
                      : 'वटुः प्रदेशं निर्वचनम् करतु'}
                  </Typography>
                </FormControl>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Grandham Details Accordion */}
      <Accordion
        expanded={grandhamExpanded}
        onChange={() => parikshaType.length > 0 && setGrandhamExpanded(!grandhamExpanded)}
        disabled={parikshaType.length === 0}
        sx={{ mb: 2 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: (theme) => theme.palette.grey[50],
            borderLeft: isGrandhamComplete
              ? (theme) => `4px solid ${theme.palette.success.main}`
              : (theme) => `4px solid ${theme.palette.error.main}`,
            "&:hover": {
              backgroundColor: (theme) => theme.palette.action.hover,
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {isGrandhamComplete ? (
              <CheckCircleOutlineIcon sx={{ color: "success.main" }} />
            ) : (
              <HourglassEmptyIcon sx={{ color: "error.main" }} />
            )}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {grandhamLabels[lang].accordionTitle}{" "}
                {isGrandhamComplete ? `(${vatuAccordionLabels[lang].complete})` : `(${vatuAccordionLabels[lang].pending})`}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {isGrandhamComplete
                  ? vatuAccordionLabels[lang].allFieldsCompleted
                  : vatuAccordionLabels[lang].fillAllFields}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ maxWidth: 900, mx: "auto", p: 1 }}>
            {/* Grandham Details grouped by Pariksha Type: for each selected type show Present then Previous */}
            {parikshaType.length > 0 ? (
              parikshaType.map((type, index) => (
                <Box key={type} sx={{ mb: 3 }}>
                  {index > 0 && <Box sx={{ my: 3, borderTop: "1px dashed", borderColor: "grey.300" }} />}

                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, fontSize: '1.05rem', color: '#000' }}>
                    {parikshaTypeOptions.find(opt => opt.value === type)?.label[lang]}
                  </Typography>

                  {/* Current/Present Exam Section */}
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, fontSize: '1.06rem', color: 'primary.main', textDecoration: 'underline' }}>
                    {grandhamLabels[lang].presentTitle}
                  </Typography>
                  {type === 'lakshanam' && (
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={lakshanamCurrentMode}
                      onChange={(_, value) => handleLakshanamVariantChange('current', value)}
                      sx={{ mb: 1.5 }}
                    >
                      {Object.entries(grandhamTreeConfig.lakshanam.variantLabels).map(([variantKey, variant]) => (
                        <ToggleButton key={variantKey} value={variantKey} sx={{ px: 2, textTransform: 'none' }}>
                          {variant[lang] || variant.english}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  )}
                  <GrandhamTreeData
                    data={grandhamTreeConfig[type]?.data}
                    labels={grandhamTreeConfig[type]?.labels}
                    lang={lang}
                    selections={type === 'lakshanam'
                      ? getLakshanamVariantSelections('current', lakshanamCurrentMode)
                      : grandhamSelections[type]?.current || {}}
                    currentYear={currentGrandhamYear}
                    onSelectionsChange={(selections) => updateGrandhamSelections('current', type, selections)}
                  />

                  <Box sx={{ my: 2 }} />

                  {/* Previous Exam Section */}
                  <Typography variant="subtitle1" sx={{ mb: 2, mt: 2, fontWeight: 700, fontSize: '1.06rem', color: 'primary.main', textDecoration: 'underline' }}>
                    {grandhamLabels[lang].previousTitle}
                  </Typography>
                  {type === 'lakshanam' && (
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={lakshanamPreviousMode}
                      onChange={(_, value) => handleLakshanamVariantChange('previous', value)}
                      sx={{ mb: 1.5 }}
                    >
                      {Object.entries(grandhamTreeConfig.lakshanam.variantLabels).map(([variantKey, variant]) => (
                        <ToggleButton key={variantKey} value={variantKey} sx={{ px: 2, textTransform: 'none' }}>
                          {variant[lang] || variant.english}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  )}
                  <GrandhamTreeData
                    data={grandhamTreeConfig[type]?.data}
                    labels={grandhamTreeConfig[type]?.labels}
                    lang={lang}
                    selections={type === 'lakshanam'
                      ? getLakshanamVariantSelections('previous', lakshanamPreviousMode)
                      : grandhamSelections[type]?.previous || {}}
                    onSelectionsChange={(selections) => updateGrandhamSelections('previous', type, selections)}
                    previous
                  />
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                {lang === 'telugu' ? 'దయచేసి పరీక్ష వివరాలు ఎంచుకోండి' : 'कृपया परीक्षा विवरणानि चयनतु'}
              </Typography>
            )}

            {/* Signatures Section - Moved to Second Accordion */}
            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: "2px solid",
                borderColor: "grey.200",
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, color: "primary.main" }}>
                {lang === "telugu" ? "సంతకాలు" : "हस्ताक्षराणि"}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: user?.role === 'PathashalaAdmin' ? "repeat(3, 1fr)" : "repeat(2, 1fr)" },
                  gap: 2,
                }}
              >
                <SignatureCanvas
                  label={lang === "telugu" ? "విద్యార్థి సంతకం" : "वटुः हस्ताक्षरम्"}
                  value={vatu_signature}
                  onChange={setVatuSignature}
                  required
                  disabled={isReadOnly && !canAdminEditFiles}
                />
                <SignatureCanvas
                  label={
                    lang === "telugu" ? "గురువుగారి సంతకం" : "गुरोः हस्ताक्षरम्"
                  }
                  value={guruvu_signature}
                  onChange={setGuruvuSignature}
                  required
                  disabled={isReadOnly && !canAdminEditFiles}
                />
                {(user?.role === "PathashalaAdmin" ||  
                (['Admin', 'SuperUser'].includes(user?.role) && editVatu && 
                (pathasala?.pathasala_id !== gurukulam_patahasala_id || pathasala.pathasala_name !== 'గురుకులం'))) &&
                   (
                  <SignatureCanvas
                    label={
                      lang === "telugu"
                        ? "పాఠశాల యాజమాన్యం వారి సంతకం"
                        : "विद्यालय प्रबन्धन के हस्ताक्षरम्"
                    }
                    value={pathasala_signature}
                    onChange={setPathasalaSignature}
                    required
                    disabled={isReadOnly && !canAdminEditFiles}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Save and Submit Buttons - Outside Accordions */}
      <Box
        sx={{
          mt: 3,
          mb: 3,
          display: "flex",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Button 
          variant="contained"
          onClick={handleSave} 
          disabled={isSaveDisabled()}
          size="large"
          sx={{
            borderRadius: '50px',
            px: 4,
            backgroundColor: isSaveDisabled() ? 'rgba(0, 32, 96, 0.3)' : '#7f4b2d',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: isSaveDisabled() ? 'rgba(0, 32, 96, 0.3)' : '#001040'
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(0, 32, 96, 0.3)',
            }
          }}
          title={completed ? 'This form has been completed and cannot be edited' : (isAdminOnly && showAdminFieldsToday && !isAdminAccordionComplete) ? 'Please complete the admin options to save' : (isComplete && isGrandhamComplete) ? 'Both accordions are completed. Click Submit to proceed.' : ''}
        >
          {saving ? "Saving..." : isSaveDisabled() ? "Completed" : "Save"}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isComplete || !isGrandhamComplete || submitting || isReadOnly}
          size="large"
          sx={{
            borderRadius: '50px',
            px: 4,
            backgroundColor: '#7f4b2d',
            '&:hover': {
              backgroundColor: '#001040',
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(0, 32, 96, 0.3)',
            }
          }}
        >
          {submitting ? "Submitting..." : "Submit"}
        </Button>
      </Box>

      {/* Gurukula Vidyarthi Information Dialog */}
      <Dialog open={showGurukshaDialog} onClose={() => setShowGurukshaDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: 'primary.main' }}>
          {lang === 'telugu' ? 'గురుకుల విద్యార్థి సమాచారం' : 'गुरुकुल विद्यार्थी जानकारी'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 2, lineHeight: 1.8 }}>
            {lang === 'telugu' 
              ? 'గురువు గారి ఇంట్లో వేదాలను చదువుకుంటున్న విద్యార్థి, ఏ పాఠశాలకు సంబంధం లేనివారు. మీరు పాఠశాలలో చదువుతున్నట్లైతే, దయచేసి నిష్క్రమించి మీ పాఠశాల యజమాని ద్వారా పరీక్షకు నమోదు చేసుకోగలరు'
              : 'गुरु के घर पर रहकर वेदों का अध्ययन करने वाली छात्र, किसी भी पाठशाला से संबंधित नहीं।'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGurukshaDialog(false)} color="primary" variant="contained">
            {lang === 'telugu' ? 'అలాగే' : 'ठीक है'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Guruvu Name Confirmation Dialog */}
      <Dialog open={showDeleteGuruvuDialog} onClose={() => { setShowDeleteGuruvuDialog(false); setGuruvuToDelete(''); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>
          {lang === 'telugu' ? 'గురువుగారి పేరు తొలగించు' : 'गुरोः नाम विलोपनम्'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 2, lineHeight: 1.8 }}>
            {lang === 'telugu' 
              ? `"${guruvuToDelete}" తొలగించబడుతుంది. తదుపరి నమోదుల కోసం మీరు పేరును మాన్యువల్‌గా టైప్ చేయాలి.`
              : `"${guruvuToDelete}" will be deleted. You will need to type the name manually for future registrations.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowDeleteGuruvuDialog(false); setGuruvuToDelete(''); }}>
            {lang === 'telugu' ? 'రద్దు' : 'Cancel'}
          </Button>
          <Button onClick={handleDeleteGuruvuName} color="error" variant="contained">
            {lang === 'telugu' ? 'తొలగించు' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kramantham Attestation Dialog for విశేష క్రమాంతం / విశేష ఘనం */}
      <Dialog open={showKramanthamDialog} onClose={() => setShowKramanthamDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: 'primary.main' }}>
          {lang === 'telugu' ? 'పట్టా పరీక్ష అంగీకారం' : 'पाट्टापरीक्षा प्रमाणपत्रम्'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2, fontWeight: 600 }}>
            {pendingKramantham === 'vishesha_ghana'
              ? (lang === 'telugu' ? 'విశేష ఘనం - పరీక్ష నియమాలు' : 'विशेषघनम् - प्रश्ननियमाः')
              : (lang === 'telugu' ? 'విశేష క్రమాంతం - పరీక్ష నియమాలు' : 'विशेषक्रमन्थम् - प्रश्ननियमाः')
            }
          </Typography>
          {pendingKramantham === 'vishesha_ghana' ? (
            <Box component="div" sx={{ whiteSpace: 'pre-line' }}>
              {lang === 'telugu' ? (
                `ప్రశ్నలు:\n\t•\tజట & ఘన భాగాలలో ఒక వరుస కాకుండా ఎక్కడైనా సరే ఆమూలాగ్రంలో 50 పనసలు\n\t•\tబ్రాహ్మణారణ్యకంలో 10 పనసలు (మొత్తం 60 పనసలు) అడగడం జరుగుతుంది\n\t•\t4 వ దోషం రాగానే పరీక్ష నిలుపుట జరుగును\n\t•\tదోషాలుగా పరిగణించబడేవి: అపస్వరం, అపాక్షరం, బారు, కౌళ్లు, అపసంధులు` 
              ) : (
                `प्रश्नाः:\n\t•\tजटा च घन भागेषु, पङ्क्तेः समानं न भवति चेत्, यत्र किञ्चित् स्थानम् अस्ति, अमूलाग्रे 50 पन्शाः पृच्छ्यन्ते\n\t•\tब्रह्मणारण्यके 10 पन्शाः पृच्छ्यन्ते (समष्ट्याः 60 पन्शाः)\nचतुर्थदोषः छात्रपरिक्षां व्यवधास्यति\nदोषाः इति गण्यन्ते: अपस्वरम्, अपाक्षरम्, बारु, कौःलि:, अपसन्धयः` 
              )}
            </Box>
          ) : (
            <Box component="div" sx={{ whiteSpace: 'pre-line' }}>
              {lang === 'telugu' ? (
                `ప్రశ్నలు:\n•\tమూల పద క్రమ భాగాలలో ఒక వరుస కాకుండా ఎక్కడైనా సరే ఆమూలాగ్రం లో 100 పనసలు అడగడం జరుగుతుంది\n\t•\t4 వ దోషం రాగానే పరీక్ష నిలుపుట జరుగును`
              ) : (
                `प्रश्नाः:\n•\tमूलपदक्रमभागेषु, पङ्क्तेः समानं न भवति चेत्, यत्र अपि स्थानम् अस्ति, अमूलाग्रे 100 पन्शाः पृच्छ्यन्ते\nचतुर्थदोषः छात्रपरिक्षां व्यवधास्यति`
              )}
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={attestationChecked} onChange={(e) => setAttestationChecked(e.target.checked)} />}
              label={lang === 'telugu'
                ? `నేను ఈ పట్టా పరీక్షకు దరఖాస్తు చేసే నియమాలు చదివి తెలుసుకున్నాను మరియు నేను అర్హుడిని మరియు ఈ పట్టా పరీక్ష ఇవ్వడానికి సిద్ధంగా ఉన్నాను.`
                : `अहं एतत् पाट्टापरीक्षायै आवेदननियमाः पठितवान् स्म तथा अहं अर्हः अस्मि च एषां पाट्टापरीक्षां प्रयत्नं कर्तु इच्छामि।`
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowKramanthamDialog(false); setPendingKramantham(''); setAttestationChecked(false); }}>
            {lang === 'telugu' ? 'రద్దు చేయి' : 'रद्द करो'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!attestationChecked}
            onClick={() => {
              // confirm selection
              setKrishnaYajurvedaKramantham(pendingKramantham);
              setShowKramanthamDialog(false);
              setPendingKramantham('');
              setAttestationChecked(false);
              // Clear grandhams so cascade can run fresh with new kramantham selection
              setKrishnaYajurvedaCurrentGrandhams({});
            }}
          >
            {lang === 'telugu' ? 'అంగీకరించు' : 'स्वीकुरु'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submission success dialog shown after successful Submit */}
      <Dialog open={submitSuccessOpen} onClose={() => setSubmitSuccessOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleOutlineIcon sx={{ color: 'success.main' }} />
          {lang === 'telugu' ? 'సమర్పణ విజయవంతం' : 'समर्पणं सफलम्'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1, lineHeight: 1.6 }}>
            {lang === 'telugu'
              ? 'విద్యార్థి వివరాలు ప్రస్తుత సంవత్సరానికి పరీక్షల కోసం సమర్పించబడ్డాయి. మేము సమర్పించిన వివరాలను సమీక్షించి, ఏదైనా స్పష్టత అవసరం అయితే మిమ్మల్ని సంప్రదిస్తాము.'
              : 'वटु विवरणानि वर्तमानवर्षस्य परीक्षायै समर्पितानि। वयम् समर्पितानि विवरणानि समीक्ष्यामः तथा यदि स्पष्टीकरणस्य आवश्यकता भवति तर्हि वयम् पुनः सम्प्रेषयामः।'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSubmitSuccessOpen(false);
              // Redirect to Pariksha home
              window.location.href = '/parikshaMgmt';
            }}
            variant="contained"
            color="primary"
          >
            {lang === 'telugu' ? 'అలాగే' : 'सम्यक्'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      </>
      )}
    </>
  );
}
